import React, { createContext, useContext, useState, useMemo, useCallback } from "react";

const dict = {
  en: {
    // Navigation
    "nav_for_tenants": "For Tenants",
    "nav_for_landlords": "For Landlords",
    "nav_for_brokers": "For Brokers & Agents",
    "nav_trust": "Trust",
    "nav_about": "About Us",
    "nav_contact": "Contact",
    "nav_login": "Login",
    "nav_get_started": "Get Started Free",

    // Hero & Main
    "no_deposit_fights": "No deposit fights.",
    "renting_without_fights": "Renting, without the fights.",
    "hero_sub": "One platform for every party in an Indian rental — verified tenants, protected deposits, court-valid agreements, and AI-powered dispute resolution. Fair for landlords, tenants, and brokers.",
    "get_started": "Get Started Free",
    "see_how": "See How It Works",
    "login": "Login",
    "sign_up": "Sign up",
    "create_free_account": "Create Free Account",

    // For Tenants
    "your_deposit_deserves": "Your deposit deserves to come back.",
    "deposit_protected": "₹45,000+ deposits protected",
    "avg_resolution": "7 days avg resolution",
    "success_rate": "99.2% disputes resolved",
    "deposit_in_escrow": "Deposit in Escrow",
    "proof_locked": "Proof Locked on Day One",
    "you_approve": "You Approve Every Deduction",
    "disputes_resolved": "ORE-AI Instant Verdict",

    // For Landlords
    "rent_with_confidence": "Rent with confidence.",
    "properties_listed": "12,000+ verified properties",
    "avg_deduction": "₹8,500 avg deduction",
    "paperwork_free": "100% digital process",
    "verified_tenants": "Verified Tenants",
    "court_valid": "Court-Valid Agreements",
    "property_documented": "Property Documented",
    "clean_exits": "Clean Exits",

    // For Brokers
    "close_more_deals": "Close more deals. Argue less.",
    "brokers_partnered": "8,500+ active agents",
    "avg_earnings": "₹4,500 per deal",
    "faster_closures": "5 min setup to signature",
    "earn_on_deals": "Earn on Every Deal",

    // Trust & Security
    "we_protect_everyone": "We protect everyone.",
    "regulated_escrow": "Regulated Escrow",
    "legal_agreements": "Legal Agreements",
    "timestamped_records": "Timestamped Records",

    // About
    "renting_broken": "Renting shouldn't be this broken.",
    "we_started": "We started because we got angry.",
    "fair": "Fair",
    "fast": "Fast",
    "transparent": "Transparent",

    // Contact
    "get_in_touch": "Get in Touch",
    "send_message": "Send us a message",
    "email": "Email",
    "whatsapp": "WhatsApp",
    "office": "Office",
    "full_name": "Full Name",
    "email_address": "Email Address",
    "message": "Message",

    // Footer
    "terms": "Terms",
    "privacy": "Privacy",
    "copyright": "© 2025 Orello. All rights reserved.",

    // Common
    "sound_familiar": "Sound familiar?",
    "simple_secure_done": "Simple. Secure. Done.",
    "built_for_everyone": "Built for everyone in the rental",
    "why_orello": "Why Orello?",
    "simple_pricing": "Simple pricing",
    "start_today": "Start today. Free.",
    "tenants": "Tenants",
    "landlords": "Landlords",
    "brokers": "Brokers",
    "made_for_india": "Made for India",
    "how_it_works": "How it works",
    "language": "Language",

    // Hero section
    "hero_built_for": "Built for Indian Rentals",
    "hero_cta_get_started": "Get Started Free",
    "hero_cta_see_how": "See How It Works",
    "hero_ticker_setup": "₹0 setup fee",
    "hero_ticker_deal": "5 min to create a deal",
    "hero_ticker_disputes": "ORE-AI delivers instant dispute verdicts",
    "hero_ticker_legal": "100% legally valid agreements",

    // Ticker bar
    "ticker_escrow": "Deposit in Escrow",
    "ticker_legal": "Legally Valid Agreements",
    "ticker_aadhaar": "Aadhaar-Verified Tenants",
    "ticker_condition": "Timestamped Condition Reports",
    "ticker_dispute": "ORE-AI Dispute Resolution",
    "ticker_nowhatsapp": "No WhatsApp Arguments",
    "ticker_3way": "3-Way Verified Deals",
    "ticker_india": "Built for India",

    // Problem section
    "problem_label": "The Problem",
    "problem_heading": "Sound familiar?",
    "problem_01_title": "Deposit just... vanished",
    "problem_01_body": "Paid ₹80,000. Got back ₹20,000. No itemised list, no photos, no explanation. Just a number.",
    "problem_01_tag": "Happens to 1 in 3 tenants",
    "problem_02_title": "No proof, no case",
    "problem_02_body": "Tenant says the scratch was already there. Landlord says it wasn't. Neither has photos. Nobody wins.",
    "problem_02_tag": "No documentation = no resolution",
    "problem_03_title": "WhatsApp is not a legal document",
    "problem_03_body": "Every move-out becomes a thread of arguments, screenshots, and silence. Then someone just stops replying.",
    "problem_03_tag": "The broker gets blamed anyway",

    // How it works / Renting Cycle
    "how_label": "The Orello Way",
    "how_heading_1": "One rental.",
    "how_heading_2": "Six points of protection.",
    "how_body": "Every Indian rental follows the same risky path — for all three parties. Orello protects landlords, tenants, and brokers at every step, from the first agreement to the final settlement.",
    "how_step_01_title": "Find your agent",
    "how_step_01_desc": "Browse verified brokers and agents on Orello. Real ratings, real deals closed, transparent commissions.",
    "how_step_02_title": "Sign the agreement",
    "how_step_02_desc": "E-stamped, court-valid rental agreement. All parties sign digitally. No notary, no printer, no chasing signatures.",
    "how_step_03_title": "Deposit secured in escrow",
    "how_step_03_desc": "Tenant pays — money sits with Orello, not the landlord. Locked in escrow until both parties agree to release.",
    "how_step_04_title": "Move-in report locked",
    "how_step_04_desc": "Walls, floors, fixtures — photographed, timestamped, agreed by both sides. No 'it was already like that' arguments later.",
    "how_step_05_title": "Worry-free tenancy",
    "how_step_05_desc": "Your deposit is held by Orello throughout — locked, protected, untouchable. Track the full rental lifecycle from agreement to checkout in one dashboard.",
    "how_step_06_title": "Settle without drama",
    "how_step_06_desc": "Move-out report compared room-by-room with move-in. Genuine deductions agreed on the platform. If disputed, ORE-AI reviews all evidence and gives an instant verdict — neutral, evidence-based, binding.",
    "how_bottom_pill": "Every step is digital, timestamped, and legally binding.",

    // Why Orello benefits
    "why_label": "Why Orello",
    "why_heading_1": "Built for the way",
    "why_heading_2": "Indians actually rent.",
    "why_b1_title": "Deposit held by Orello",
    "why_b1_desc": "The deposit is held securely in Orello's escrow — not with either party. Released only when both sides agree.",
    "why_b2_title": "Set up in 15 minutes",
    "why_b2_desc": "Agreement, KYC, deposit, condition report — all done in one sitting from your phone.",
    "why_b3_title": "ORE-AI dispute engine",
    "why_b3_desc": "India's first AI built specifically for rental disputes. Reviews evidence, applies local market rates, gives an instant verdict.",
    "why_b4_title": "Court-valid agreements",
    "why_b4_desc": "E-stamped under Indian rental law. Digitally signed by all parties. Holds up in court without printers or notaries.",
    "why_b5_title": "Proof that doesn't fade",
    "why_b5_desc": "Room-by-room move-in photos timestamped on day one. Move-out comparison is side-by-side and automatic.",
    "why_b6_title": "Multi-tenant support",
    "why_b6_desc": "Multiple tenants on one deal? Each party pays their share. Orello splits the deposit and platform fee equally — no manual calculations.",

    // Persona: Tenants
    "persona_tenant_label": "For Tenants",
    "persona_tenant_headline": "your deposit\nis yours.",
    "persona_tenant_body": "landlords hold your money for months and return it on their terms. orello holds it in escrow — locked, timestamped, and released only when you agree.",
    "persona_tenant_f1_t": "deposit in escrow",
    "persona_tenant_f1_d": "your money is held safely with orello — not in the landlord's account. they can't touch it without your sign-off.",
    "persona_tenant_f2_t": "proof locked on day one",
    "persona_tenant_f2_d": "photos, videos, and condition notes sealed with a timestamp at move-in. no one can say it was like that later.",
    "persona_tenant_f3_t": "you approve every deduction",
    "persona_tenant_f3_d": "review each claim before a paisa moves. dispute anything you disagree with.",
    "persona_tenant_f4_t": "ORE-AI reviews your dispute",
    "persona_tenant_f4_d": "if you can't agree, ORE-AI — orello's purpose-built dispute engine — reviews check-in photos, checkout evidence, and local market rates. instant verdict. no lawyers, no courts.",
    "persona_tenant_cta": "sign up as a tenant",

    // Persona: Landlords
    "persona_landlord_label": "For Landlords",
    "persona_landlord_headline": "your property.\nyour terms.\nzero surprises.",
    "persona_landlord_body": "most landlords lose money not from bad tenants — but from bad documentation. orello gives you verified tenants, court-valid agreements, and room-by-room evidence before a single key is handed over.",
    "persona_landlord_f1_t": "know who's moving in",
    "persona_landlord_f1_d": "every tenant is aadhaar-verified before they get the keys. not a photo, not a forwarded id — actual kyc.",
    "persona_landlord_f2_t": "agreements that hold up in court",
    "persona_landlord_f2_d": "e-stamped, digitally signed rental agreement. legally valid anywhere in india. downloadable anytime. no notary runs.",
    "persona_landlord_f3_t": "your flat, documented before they move in",
    "persona_landlord_f3_d": "room-by-room condition report with photos, locked at move-in. undeniable evidence if there's damage at move-out.",
    "persona_landlord_f4_t": "clean exits, every time",
    "persona_landlord_f4_d": "both sides settle deductions on the platform. no last-day arguments, no chasing on whatsapp, no awkward standoffs.",
    "persona_landlord_cta": "sign up as a landlord",

    // Persona: Brokers
    "persona_broker_label": "For Brokers & Agents",
    "persona_broker_headline": "close more.\nargue less.",
    "persona_broker_body": "you spend half your time doing paperwork and the other half mediating deposit fights. orello handles both — so you can focus on closing deals.",
    "persona_broker_f1_t": "agreements in minutes",
    "persona_broker_f1_d": "create a legally valid, e-stamped rental agreement in one flow. kyc, deposit, signatures — done before the client leaves.",
    "persona_broker_f2_t": "access everything, anywhere",
    "persona_broker_f2_d": "all your agreements, reports, and deal status in one dashboard — on any device, any time, stress-free.",
    "persona_broker_f3_t": "stop being the middleman",
    "persona_broker_f3_d": "disputes go to orello, not your phone. tenants and landlords resolve it on the platform — you stay out of it.",
    "persona_broker_f4_t": "earn as you close",
    "persona_broker_f4_d": "charge your brokerage transparently through the platform — from tenant, landlord, or split. tracked automatically, paid to your orello wallet. refer agents and earn referral bonuses.",
    "persona_broker_cta": "sign up as a broker",

    // Proof section
    "proof_label": "The Proof",
    "proof_heading_1": "The Proof",
    "proof_heading_2": "writes itself.",
    "proof_stat1_label": "to get started",
    "proof_stat1_sub": "free for everyone",
    "proof_stat2_label": "to create a deal",
    "proof_stat2_sub": "kyc to signature",
    "proof_stat3_label": "ORE-AI verdict",
    "proof_stat3_sub": "evidence-based decision",
    "proof_stat4_label": "legally valid",
    "proof_stat4_sub": "e-stamped & signed",
    "proof_stat5_label": "proof locked",
    "proof_stat5_sub": "timestamped forever",

    // FOMO / Social proof
    "fomo_label": "What People Are Saying",
    "fomo_heading": "Real people. Real deposits.\nReal resolution.",
    "fomo_stat1_label": "of rentals closed without a dispute",
    "fomo_stat2_label": "ORE-AI delivers dispute verdict",
    "fomo_stat3_label": "digitally signed agreements",
    "fomo_stat4_label": "platform fee to get started",
    "review_1_quote": "I was sure I'd lose my deposit over a scratch on the wall. Orello's move-in photos saved me ₹45,000.",
    "review_1_role": "Tenant",
    "review_1_location": "Bandra, Mumbai",
    "review_2_quote": "My last tenant left without notice. With Orello I had a signed agreement, documented condition, and got my dues settled in 8 days.",
    "review_2_role": "Landlord",
    "review_2_location": "Kothrud, Pune",
    "review_3_quote": "I close 6–8 deals a month. Orello makes every one of them clean — no WhatsApp arguments, no chasing signatures.",
    "review_3_role": "Broker",
    "review_3_location": "Andheri, Mumbai",
    "review_4_quote": "Landlord tried to deduct ₹30,000 for repainting. Orello showed the walls were fine at move-in. Got every rupee back.",
    "review_4_role": "Tenant",
    "review_4_location": "Koramangala, Bangalore",
    "review_5_quote": "Finally a platform that protects both sides. My tenant felt safe paying the deposit, I felt safe receiving it.",
    "review_5_role": "Landlord",
    "review_5_location": "Adyar, Chennai",
    "review_6_quote": "Referred 3 brokers last month, earned ₹750 in bonuses. And my clients keep coming back because they trust the process.",
    "review_6_role": "Agent",
    "review_6_location": "Jubilee Hills, Hyderabad",

    // Broker CTA section
    "broker_cta_label": "For Brokers & Agents",
    "broker_cta_heading_1": "your property management platform.",
    "broker_cta_heading_2": "free to start.",
    "broker_cta_body": "agreements, kyc, deposits, condition reports, dispute resolution — every part of a rental deal, from one dashboard.",
    "broker_cta_btn": "create your free account",
    "broker_cta_fine": "no credit card. no setup fee. unlimited agreements.",

    // Final CTA section
    "final_cta_heading_1": "your next rental.",
    "final_cta_heading_2": "protected.",
    "final_cta_body": "no credit card. no setup fee. ready in minutes.",
    "final_cta_btn": "create free account",

    // Login page
    "login_welcome": "Welcome back",
    "login_subtitle_default": "Sign in with your phone or email and password.",
    "login_subtitle_tenant": "Sign in to protect your deposit",
    "login_subtitle_landlord": "Sign in to verify your tenant",
    "login_subtitle_broker": "Sign in to earn commission on every deal",
    "login_phone_label": "Phone or email",
    "login_phone_placeholder": "9876543210 or you@example.com",
    "login_password_label": "Password",
    "login_forgot": "Forgot password?",
    "login_btn": "Sign In",
    "login_btn_loading": "Signing in…",
    "login_no_account": "Don't have an account?",
    "login_signup": "Sign up",

    // Agreement Form Builder
    "form_step_minus1_title": "Select Your State",
    "form_step_0_title": "Step 0: Choose Your Agreement Type",
    "form_step_0_description": "Select the agreement format that best fits your needs. Each offers different legal validity and cost levels.",
    "form_step_05_title": "Step 0.5: Who Pays Stamp Duty & Registration?",
    "form_step_05_description": "Choose who bears the stamp duty and registration fees for the e-stamped agreement",
    "form_step_1_title": "Step 1: Landlord (Property Owner) Details",
    "form_step_2_title": "Step 2: Tenant Details",
    "form_step_3_title": "Step 3: Property Details",
    "form_step_4_title": "Step 4: Rental Terms",
    "form_step_5_title": "Step 5: Agreement Terms & Duration",
    "form_step_6_title": "Step 6: Terms & Conditions",
    "form_step_7_title": "Step 7: Jurisdiction & Registration",
    "form_step_8_title": "Step 8: Share Agreement",

    // Agreement Type Options
    "form_estamped_title": "E-Stamp Agreement (Orello)",
    "form_estamped_desc": "Legally stamped • ₹299 + stamp duty",
    "form_estamped_benefit": "Legally valid & court-admissible for any lease duration",

    "form_eagreement_title": "E-Agreement (Orello)",
    "form_eagreement_desc": "Digital agreement • ₹99 flat",
    "form_eagreement_benefit": "Not legally court-valid. Best for <11 months. Opt for E-Stamp if disputes anticipated.",

    "form_traditional_title": "Traditional Paper (External)",
    "form_traditional_desc": "Your own agreement • Orello deposit protection only",
    "form_traditional_benefit": "Use your own/broker-made agreement. Orello manages deposit only.",

    // Form Fields - Common
    "form_full_name": "Full Name",
    "form_full_name_placeholder": "Enter full name",
    "form_age": "Age",
    "form_age_placeholder": "Enter age",
    "form_email": "Email Address",
    "form_email_placeholder": "Enter email",
    "form_phone": "Phone Number",
    "form_phone_placeholder": "10-digit phone number",
    "form_address": "Full Address",
    "form_address_placeholder": "Enter complete address",
    "form_pan": "PAN: AAAAA NNNN C",
    "form_pan_placeholder": "e.g., ABCDE1234F",
    "form_fathersname": "Father's/Spouse's Name",
    "form_fathersname_placeholder": "Enter name",

    // Property Details
    "form_property_type": "Property Type",
    "form_residential": "Residential",
    "form_commercial": "Commercial",
    "form_property_state": "Property State",
    "form_property_city": "Property City",
    "form_property_address": "Property Address",
    "form_property_address_placeholder": "Enter complete property address",
    "form_property_bedrooms": "BHK Configuration",
    "form_property_furnished": "Furnishing Status",
    "form_furnished": "Fully Furnished",
    "form_semi_furnished": "Semi-Furnished",
    "form_unfurnished": "Unfurnished",

    // Rental Terms
    "form_monthly_rent": "Monthly Rent (₹)",
    "form_deposit_amount": "Deposit Amount (₹)",
    "form_lock_in_months": "Lock-in Period (Months)",
    "form_start_date": "Start Date",
    "form_end_date": "End Date (Auto-calculated)",
    "form_end_date_help": "System auto-calculates based on start date + duration. You can override it by selecting a different date.",
    "form_lease_duration": "Lease Duration (Years)",
    "form_lease_duration_months": "Lease Duration (Months)",

    // Fee Payer Options
    "form_tenant_pays_full": "Tenant pays full amount",
    "form_tenant_pays_desc": "Tenant pays deposit + stamp duty & registration",
    "form_landlord_pays_full": "Landlord pays full amount",
    "form_landlord_pays_desc": "Landlord bears stamp duty & registration separately",
    "form_split_50": "Split 50-50",
    "form_split_50_desc": "Tenant and landlord each pay half of stamp duty & registration",

    // Utilities & Maintenance
    "form_utilities": "Utilities & Maintenance",
    "form_electricity": "Electricity",
    "form_electricity_landlord": "Electricity: Landlord pays",
    "form_electricity_tenant": "Electricity: Tenant pays",
    "form_water": "Water",
    "form_water_landlord": "Water: Landlord pays",
    "form_water_tenant": "Water: Tenant pays",
    "form_gas": "Gas",
    "form_gas_landlord": "Gas: Landlord pays",
    "form_gas_tenant": "Gas: Tenant pays",
    "form_property_tax": "Property Tax",
    "form_property_tax_landlord": "Property Tax: Landlord pays",
    "form_property_tax_tenant": "Property Tax: Tenant pays",
    "form_maintenance_charges": "Maintenance Charges (Monthly)",
    "form_maintenance_charges_placeholder": "Enter amount in ₹",

    // Buttons
    "form_back": "← Back",
    "form_next": "Next →",
    "form_submit": "Create Agreement",
    "form_cancel": "Cancel",
    "form_download_pdf": "Download PDF",
    "form_switch_estamp": "⬆️ Switch to E-Stamp",

    // Deposit Protection
    "form_deposit_protection": "Deposit Protection",
    "form_deposit_protection_yes": "Yes, I want deposit protection",
    "form_deposit_protection_no": "No, skip deposit protection",

    // Errors & Validation
    "form_error_required": "This field is required",
    "form_error_invalid_email": "Please enter a valid email address",
    "form_error_invalid_phone": "Please enter a valid 10-digit phone number",
    "form_error_invalid_pan": "Please enter a valid PAN (Format: AAAAA NNNN C)",
    "form_error_dates": "End date must be after start date",
    "form_error_select_option": "Please select an option before proceeding",

    // PDF & Sharing
    "form_pdf_title": "Agreement Preview & Download",
    "form_pdf_desc_eagreement": "This is the comprehensive agreement with all legal clauses and jurisdiction-specific terms.",
    "form_pdf_desc_estamp": "This is the comprehensive E-Stamp agreement with government stamp duty, legal clauses, and jurisdiction-specific terms.",
    "form_share_agreement": "Share Agreement",
    "form_send_invite": "Send Invitations",
    "form_registration_status": "Registration Status",
    "form_ready_to_sign": "Ready to Sign",

    // Main Dashboard UI
    "page_create_agreement": "Create Agreement",
    "page_fill_details": "Fill out the agreement details. Watch the live preview update in real-time.",
    "page_select_state": "Select State for Agreement Template",
    "page_select_state_desc": "Choose your state to load the appropriate agreement template. Different states have different legal requirements for lease agreements.",
    "page_select_state_label": "📍 Select Your State",
    "page_agreement_format": "📋 Agreement Format",
    "page_send_invites": "📧 Share Agreement & Send Invites",
    "page_pdf_preview": "📄 Agreement Preview & Download",
    "page_registration_status": "Registration Status",
    "page_landlord": "🏠 Property Owner",
    "page_tenant": "👤 Tenant",
    "page_ready_to_sign": "Ready to Sign",
    "page_registered": "✓ Registered",
    "page_not_registered": "○ Not Registered",

    // Sidebar Navigation (English)
    "sidebar_home": "Home",
    "sidebar_overview": "Overview",
    "sidebar_create_agreement": "Create Agreement",
    "sidebar_my_team": "My Team",
    "sidebar_my_deals": "My Deals",
    "sidebar_all_deals": "All Deals",
    "sidebar_my_earnings": "My Earnings",
    "sidebar_my_rental": "My Rental",
    "sidebar_ofix": "O-Fix",
    "sidebar_deposits": "Deposits",
    "sidebar_wallet": "Wallet",
    "sidebar_notifications": "Notifications",
    "sidebar_analytics": "Analytics",
    "sidebar_business_profile": "Business Profile",
    "sidebar_referral": "Referral",
    "sidebar_profile": "Profile",
    "sidebar_settings": "Settings",
    "sidebar_support": "Support",
    "sidebar_help": "Help",
  },

  hi: {
    // Navigation
    "nav_for_tenants": "किरायेदारों के लिए",
    "nav_for_landlords": "मकान मालिकों के लिए",
    "nav_for_brokers": "ब्रोकर्स और एजेंटों के लिए",
    "nav_trust": "विश्वास",
    "nav_about": "हमारे बारे में",
    "nav_contact": "संपर्क करें",
    "nav_login": "लॉगिन",
    "nav_get_started": "मुफ़्त शुरू करें",

    // Hero & Main
    "no_deposit_fights": "अब डिपॉज़िट पर कोई झगड़ा नहीं।",
    "renting_without_fights": "किराये पर लेना, झगड़ों के बिना।",
    "hero_sub": "Orello समझौते, डिपॉज़िट और संपत्ति रिपोर्ट संभालता है — सबके लिए न्याय।",
    "get_started": "मुफ़्त शुरू करें",
    "see_how": "देखें कैसे काम करता है",
    "login": "लॉगिन",
    "sign_up": "साइन अप",
    "create_free_account": "मुफ़्त खाता बनाएँ",

    // For Tenants
    "your_deposit_deserves": "आपका डिपॉज़िट वापस आने का हकदार है।",
    "deposit_protected": "₹45,000+ डिपॉज़िट सुरक्षित",
    "avg_resolution": "7 दिन औसत समाधान",
    "success_rate": "99.2% विवाद हल",
    "deposit_in_escrow": "डिपॉज़िट एस्क्रो में",
    "proof_locked": "पहले दिन सबूत लॉक",
    "you_approve": "आप हर कटौती को मंजूरी दें",
    "disputes_resolved": "10 दिन में विवाद हल",

    // For Landlords
    "rent_with_confidence": "आत्मविश्वास से किराये पर दें।",
    "properties_listed": "12,000+ सत्यापित संपत्तियां",
    "avg_deduction": "₹8,500 औसत कटौती",
    "paperwork_free": "100% डिजिटल प्रक्रिया",
    "verified_tenants": "सत्यापित किरायेदार",
    "court_valid": "न्यायालय-मान्य समझौते",
    "property_documented": "संपत्ति दस्तावेज़",
    "clean_exits": "स्वच्छ बाहर निकलना",

    // For Brokers
    "close_more_deals": "अधिक डील बंद करें। कम झगड़े।",
    "brokers_partnered": "8,500+ सक्रिय एजेंट",
    "avg_earnings": "₹4,500 प्रति डील",
    "faster_closures": "5 मिनट सेटअप से हस्ताक्षर",
    "earn_on_deals": "हर डील पर कमाएँ",

    // Trust & Security
    "we_protect_everyone": "हम सभी की रक्षा करते हैं।",
    "regulated_escrow": "विनियमित एस्क्रो",
    "legal_agreements": "कानूनी समझौते",
    "timestamped_records": "समय-चिह्नित रिकॉर्ड",

    // About
    "renting_broken": "किराये पर लेना इतना टूटा हुआ नहीं होना चाहिए।",
    "we_started": "हम शुरू हुए क्योंकि हम गुस्से में थे।",
    "fair": "न्यायसंगत",
    "fast": "तेज़",
    "transparent": "पारदर्शी",

    // Contact
    "get_in_touch": "हमसे संपर्क करें",
    "send_message": "हमें संदेश भेजें",
    "email": "ईमेल",
    "whatsapp": "व्हाट्सएप",
    "office": "कार्यालय",
    "full_name": "पूरा नाम",
    "email_address": "ईमेल पता",
    "message": "संदेश",

    // Footer
    "terms": "शर्तें",
    "privacy": "गोपनीयता",
    "copyright": "© 2025 Orello. सर्वाधिकार सुरक्षित।",

    // Common
    "sound_familiar": "जाना-पहचाना लगता है?",
    "simple_secure_done": "सरल। सुरक्षित। हो गया।",
    "built_for_everyone": "हर किसी के लिए बना",
    "why_orello": "Orello क्यों?",
    "simple_pricing": "सीधी कीमतें",
    "start_today": "आज ही शुरू करें। मुफ़्त।",
    "tenants": "किरायेदार",
    "landlords": "मकान मालिक",
    "brokers": "ब्रोकर्स",
    "made_for_india": "भारत के लिए बना",
    "how_it_works": "कैसे काम करता है",
    "language": "भाषा",

    // Hero section
    "hero_built_for": "भारतीय किराये के लिए बना",
    "hero_cta_get_started": "मुफ़्त शुरू करें",
    "hero_cta_see_how": "देखें कैसे काम करता है",
    "hero_ticker_setup": "₹0 सेटअप शुल्क",
    "hero_ticker_deal": "5 मिनट में डील बनाएँ",
    "hero_ticker_disputes": "10 दिन में निपटाया",
    "hero_ticker_legal": "100% कानूनी रूप से वैध समझौते",

    // Ticker bar
    "ticker_escrow": "डिपॉज़िट एस्क्रो में",
    "ticker_legal": "कानूनी रूप से वैध समझौते",
    "ticker_aadhaar": "आधार-सत्यापित किरायेदार",
    "ticker_condition": "टाइमस्टैम्प्ड स्थिति रिपोर्ट",
    "ticker_dispute": "न्यायसंगत विवाद समाधान",
    "ticker_nowhatsapp": "कोई व्हाट्सएप झगड़ा नहीं",
    "ticker_3way": "3-पक्षीय सत्यापित डील",
    "ticker_india": "भारत के लिए बना",

    // Problem section
    "problem_label": "समस्या",
    "problem_heading": "जाना-पहचाना लगता है?",
    "problem_01_title": "डिपॉज़िट बस... गायब हो गया",
    "problem_01_body": "₹80,000 दिए। ₹20,000 वापस मिले। कोई सूची नहीं, कोई फ़ोटो नहीं, कोई स्पष्टीकरण नहीं। बस एक नंबर।",
    "problem_01_tag": "हर 3 में से 1 किरायेदार के साथ होता है",
    "problem_02_title": "कोई सबूत नहीं, कोई केस नहीं",
    "problem_02_body": "किरायेदार कहता है खरोंच पहले से थी। मालिक कहता है नहीं थी। किसी के पास फ़ोटो नहीं। कोई नहीं जीतता।",
    "problem_02_tag": "दस्तावेज़ नहीं = कोई समाधान नहीं",
    "problem_03_title": "व्हाट्सएप कोई कानूनी दस्तावेज़ नहीं है",
    "problem_03_body": "हर मूव-आउट एक बहस की जंजीर बन जाती है — स्क्रीनशॉट, दलीलें, और फिर चुप्पी। फिर कोई जवाब देना बंद कर देता है।",
    "problem_03_tag": "वैसे भी ब्रोकर को दोष मिलता है",

    // How it works
    "how_label": "Orello का तरीका",
    "how_heading_1": "एक किराया।",
    "how_heading_2": "सुरक्षा के छह पड़ाव।",
    "how_body": "हर भारतीय किराये का रास्ता एक जैसा जोखिम भरा होता है। Orello हर कदम पर आपकी रक्षा करता है — सही जगह ढूँढने से लेकर डिपॉज़िट वापस पाने तक।",
    "how_step_01_title": "अपना एजेंट खोजें",
    "how_step_01_desc": "Orello पर सत्यापित ब्रोकर और एजेंट ब्राउज़ करें। असली रेटिंग, असली डील, पारदर्शी कमीशन।",
    "how_step_02_title": "समझौते पर हस्ताक्षर करें",
    "how_step_02_desc": "ई-स्टैम्प्ड, न्यायालय-मान्य किराये का समझौता। सभी पक्ष डिजिटल रूप से हस्ताक्षर करते हैं। कोई नोटरी नहीं, कोई प्रिंटर नहीं।",
    "how_step_03_title": "डिपॉज़िट एस्क्रो में सुरक्षित",
    "how_step_03_desc": "किरायेदार पैसे देता है — पैसे Orello के पास रहते हैं, मालिक के पास नहीं। दोनों पक्षों की सहमति के बिना लॉक्ड।",
    "how_step_04_title": "मूव-इन रिपोर्ट लॉक",
    "how_step_04_desc": "दीवारें, फर्श, फिक्स्चर — फ़ोटो, टाइमस्टैम्प, दोनों की सहमति। बाद में 'पहले से ऐसा था' का कोई बहाना नहीं।",
    "how_step_05_title": "बेफिक्र किरायेदारी जीएँ",
    "how_step_05_desc": "Orello से किराया दें। सब कुछ एक जगह ट्रैक करें। नवीनीकरण रिमाइंडर, मरम्मत अनुरोध — सब एक फ़ीड में।",
    "how_step_06_title": "बिना ड्रामे के निपटान",
    "how_step_06_desc": "मूव-आउट रिपोर्ट को मूव-इन से तुलना करें। असली कटौतियाँ मिनटों में तय। विवाद? Orello निर्णय देता है — निष्पक्ष, तेज़, अंतिम।",
    "how_bottom_pill": "हर कदम डिजिटल, टाइमस्टैम्प्ड और कानूनी रूप से बाध्यकारी है।",

    // Why Orello
    "why_label": "Orello क्यों",
    "why_heading_1": "भारतीयों के वास्तविक",
    "why_heading_2": "किराये के लिए बना।",
    "why_b1_title": "पैसा सुरक्षित लॉक्ड",
    "why_b1_desc": "आपका डिपॉज़िट Orello के साथ नियमित एस्क्रो में रहता है। दोनों की सहमति पर ही जारी होता है।",
    "why_b2_title": "15 मिनट में सेटअप",
    "why_b2_desc": "समझौता, KYC, डिपॉज़िट, स्थिति रिपोर्ट — सब एक बैठक में फ़ोन से।",
    "why_b3_title": "निष्पक्ष विवाद समाधान",
    "why_b3_desc": "दोनों पक्ष सबूत देते हैं। Orello साक्ष्य के आधार पर निर्णय लेता है — कोई चाय-पक्ष हाथापाई नहीं।",
    "why_b4_title": "न्यायालय-मान्य समझौते",
    "why_b4_desc": "भारतीय किराया कानून के तहत ई-स्टैम्प। बिना प्रिंटर या नोटरी के अदालत में मान्य।",
    "why_b5_title": "सबूत जो फीका नहीं पड़ता",
    "why_b5_desc": "मूव-इन फ़ोटो और नोट्स पहले दिन टाइमस्टैम्प्ड। मूव-आउट तुलना स्वचालित।",
    "why_b6_title": "₹0 ब्रोकरेज मोलभाव",
    "why_b6_desc": "पारदर्शी कमीशन, पहले से दिखाया गया। कोई छिपी फ़ीस नहीं, कोई ब्रोकर नाटक नहीं।",

    // Persona: Tenants
    "persona_tenant_label": "किरायेदारों के लिए",
    "persona_tenant_headline": "आपका डिपॉज़िट\nआपका है।",
    "persona_tenant_body": "मकान मालिक महीनों आपका पैसा रोकते हैं और अपनी शर्तों पर वापस करते हैं। Orello इसे एस्क्रो में रखता है — लॉक्ड, टाइमस्टैम्प्ड, और आपकी सहमति से जारी।",
    "persona_tenant_f1_t": "एस्क्रो में डिपॉज़िट",
    "persona_tenant_f1_d": "आपका पैसा Orello के पास सुरक्षित है — मालिक के खाते में नहीं। वे आपकी मंज़ूरी के बिना उसे नहीं छू सकते।",
    "persona_tenant_f2_t": "पहले दिन सबूत लॉक",
    "persona_tenant_f2_d": "फ़ोटो, वीडियो और स्थिति नोट्स मूव-इन पर टाइमस्टैम्प के साथ सील। बाद में कोई बहाना नहीं।",
    "persona_tenant_f3_t": "हर कटौती आप मंज़ूर करें",
    "persona_tenant_f3_d": "एक पैसा हिलने से पहले हर दावा देखें। जो सही न लगे उसे चुनौती दें।",
    "persona_tenant_f4_t": "10 दिन में विवाद हल",
    "persona_tenant_f4_d": "सहमति न बने तो Orello सबूत देखकर 10 दिन में बाध्यकारी फ़ैसला देता है। न वकील, न अदालत।",
    "persona_tenant_cta": "किरायेदार के रूप में साइन अप करें",

    // Persona: Landlords
    "persona_landlord_label": "मकान मालिकों के लिए",
    "persona_landlord_headline": "आपकी संपत्ति।\nआपकी शर्तें।\nकोई आश्चर्य नहीं।",
    "persona_landlord_body": "ज़्यादातर मकान मालिकों को बुरे किरायेदारों से नहीं, बल्कि बुरे दस्तावेज़ीकरण से नुकसान होता है। Orello आपको सत्यापित किरायेदार, न्यायालय-मान्य समझौते और चाबी देने से पहले कमरे-दर-कमरे सबूत देता है।",
    "persona_landlord_f1_t": "जानें कौन आ रहा है",
    "persona_landlord_f1_d": "हर किरायेदार की आधार-KYC होती है चाबी मिलने से पहले। फ़ोटो नहीं, फ़ॉरवर्ड ID नहीं — असली KYC।",
    "persona_landlord_f2_t": "न्यायालय में टिकने वाले समझौते",
    "persona_landlord_f2_d": "ई-स्टैम्प्ड, डिजिटल हस्ताक्षरित किराया समझौता। भारत में कहीं भी वैध। कभी भी डाउनलोड करें। कोई नोटरी दौड़ नहीं।",
    "persona_landlord_f3_t": "मूव-इन से पहले फ्लैट दस्तावेज़ित",
    "persona_landlord_f3_d": "फ़ोटो के साथ कमरे-दर-कमरे स्थिति रिपोर्ट, मूव-इन पर लॉक्ड। मूव-आउट पर नुकसान का निर्विवाद सबूत।",
    "persona_landlord_f4_t": "हर बार स्वच्छ विदाई",
    "persona_landlord_f4_d": "दोनों पक्ष प्लेटफ़ॉर्म पर कटौतियाँ तय करते हैं। कोई अंतिम-दिन बहस नहीं, कोई व्हाट्सएप पीछा नहीं।",
    "persona_landlord_cta": "मकान मालिक के रूप में साइन अप करें",

    // Persona: Brokers
    "persona_broker_label": "ब्रोकर्स और एजेंटों के लिए",
    "persona_broker_headline": "अधिक डील।\nकम झगड़े।",
    "persona_broker_body": "आप आधा समय कागज़ी काम में और आधा डिपॉज़िट झगड़े सुलझाने में लगाते हैं। Orello दोनों संभालता है — आप डील बंद करने पर ध्यान दें।",
    "persona_broker_f1_t": "मिनटों में समझौते",
    "persona_broker_f1_d": "एक फ्लो में कानूनी, ई-स्टैम्प्ड किराया समझौता बनाएँ। KYC, डिपॉज़िट, हस्ताक्षर — क्लाइंट जाने से पहले।",
    "persona_broker_f2_t": "कहीं भी सब कुछ देखें",
    "persona_broker_f2_d": "सभी समझौते, रिपोर्ट और डील स्टेटस एक डैशबोर्ड में — किसी भी डिवाइस पर, कभी भी।",
    "persona_broker_f3_t": "बिचौलिया बनना बंद करें",
    "persona_broker_f3_d": "विवाद Orello के पास जाते हैं, आपके फ़ोन पर नहीं। किरायेदार और मालिक प्लेटफ़ॉर्म पर हल करते हैं।",
    "persona_broker_f4_t": "बंद करते जाओ, कमाते जाओ",
    "persona_broker_f4_d": "हर डिपॉज़िट सुरक्षा पर 15% कमीशन। अपने आप ट्रैक, जब चाहें निकालें।",
    "persona_broker_cta": "ब्रोकर के रूप में साइन अप करें",

    // Proof section
    "proof_label": "सबूत",
    "proof_heading_1": "सबूत",
    "proof_heading_2": "खुद बोलता है।",
    "proof_stat1_label": "शुरू करने के लिए",
    "proof_stat1_sub": "सभी के लिए मुफ़्त",
    "proof_stat2_label": "डील बनाने में",
    "proof_stat2_sub": "KYC से हस्ताक्षर",
    "proof_stat3_label": "विवाद समाधान",
    "proof_stat3_sub": "बाध्यकारी निर्णय",
    "proof_stat4_label": "कानूनी रूप से वैध",
    "proof_stat4_sub": "ई-स्टैम्प्ड और हस्ताक्षरित",
    "proof_stat5_label": "सबूत लॉक",
    "proof_stat5_sub": "हमेशा के लिए टाइमस्टैम्प",

    // FOMO / Social proof
    "fomo_label": "पूरे भारत में किरायेदारों और मालिकों का भरोसा",
    "fomo_heading": "असली लोग। असली डिपॉज़िट।\nअसली समाधान।",
    "fomo_stat1_label": "किरायेदारों को पूरा डिपॉज़िट वापस मिला",
    "fomo_stat2_label": "मालिकों ने बिना विवाद किरायेदारी पूरी की",
    "fomo_stat3_label": "विवाद 10 दिन से कम में हल",
    "fomo_stat4_label": "ब्रोकर तेज़ी से डील बंद कर रहे हैं",
    "review_1_quote": "मुझे यकीन था कि दीवार पर खरोंच के लिए मेरा डिपॉज़िट जाएगा। Orello के मूव-इन फ़ोटो ने मुझे ₹45,000 बचाए।",
    "review_1_role": "किरायेदार",
    "review_1_location": "बांद्रा, मुंबई",
    "review_2_quote": "आखिरी किरायेदार बिना बताए चला गया। Orello की वजह से मेरे पास हस्ताक्षरित समझौता, दस्तावेज़ित स्थिति थी और 8 दिन में हिसाब हो गया।",
    "review_2_role": "मकान मालिक",
    "review_2_location": "कोथरुड, पुणे",
    "review_3_quote": "मैं महीने में 6–8 डील बंद करता हूँ। Orello हर एक को साफ़ बनाता है — कोई व्हाट्सएप बहस नहीं, कोई हस्ताक्षर के लिए पीछा नहीं।",
    "review_3_role": "ब्रोकर",
    "review_3_location": "अंधेरी, मुंबई",
    "review_4_quote": "मालिक ने पेंटिंग के लिए ₹30,000 काटने की कोशिश की। Orello ने दिखाया दीवारें मूव-इन पर ठीक थीं। हर रुपया वापस मिला।",
    "review_4_role": "किरायेदार",
    "review_4_location": "कोरमंगला, बैंगलोर",
    "review_5_quote": "आखिरकार एक ऐसा प्लेटफ़ॉर्म जो दोनों की रक्षा करता है। किरायेदार को डिपॉज़िट देने में और मुझे लेने में सुरक्षा लगी।",
    "review_5_role": "मकान मालिक",
    "review_5_location": "अड्यार, चेन्नई",
    "review_6_quote": "पिछले महीने 3 ब्रोकर रेफर किए, ₹750 बोनस मिला। और ग्राहक इसलिए वापस आते हैं क्योंकि वे प्रक्रिया पर भरोसा करते हैं।",
    "review_6_role": "एजेंट",
    "review_6_location": "जुबिली हिल्स, हैदराबाद",

    // Broker CTA
    "broker_cta_label": "ब्रोकर्स और एजेंटों के लिए",
    "broker_cta_heading_1": "आपका संपत्ति प्रबंधन प्लेटफ़ॉर्म।",
    "broker_cta_heading_2": "शुरुआत मुफ़्त।",
    "broker_cta_body": "समझौते, KYC, डिपॉज़िट, स्थिति रिपोर्ट, विवाद समाधान — एक डैशबोर्ड से किराये की हर प्रक्रिया।",
    "broker_cta_btn": "अपना मुफ़्त खाता बनाएँ",
    "broker_cta_fine": "कोई क्रेडिट कार्ड नहीं। कोई सेटअप शुल्क नहीं। असीमित समझौते।",

    // Final CTA
    "final_cta_heading_1": "आपका अगला किराया।",
    "final_cta_heading_2": "सुरक्षित।",
    "final_cta_body": "कोई क्रेडिट कार्ड नहीं। कोई सेटअप शुल्क नहीं। मिनटों में तैयार।",
    "final_cta_btn": "मुफ़्त खाता बनाएँ",

    // Login page
    "login_welcome": "वापस स्वागत है",
    "login_subtitle_default": "अपने फोन या ईमेल और पासवर्ड से साइन इन करें।",
    "login_subtitle_tenant": "अपनी जमानत राशि की सुरक्षा के लिए साइन इन करें",
    "login_subtitle_landlord": "अपने किरायेदार की पुष्टि करने के लिए साइन इन करें",
    "login_subtitle_broker": "हर डील पर कमीशन कमाने के लिए साइन इन करें",
    "login_phone_label": "फोन या ईमेल",
    "login_phone_placeholder": "9876543210 या you@example.com",
    "login_password_label": "पासवर्ड",
    "login_forgot": "पासवर्ड भूल गए?",
    "login_btn": "साइन इन करें",
    "login_btn_loading": "साइन इन हो रहा है…",
    "login_no_account": "खाता नहीं है?",
    "login_signup": "साइन अप करें",

    // Agreement Form Builder
    "form_step_minus1_title": "अपनी राज्य का चयन करें",
    "form_step_0_title": "चरण 0: अपने समझौते का प्रकार चुनें",
    "form_step_0_description": "समझौते का प्रारूप चुनें जो आपकी आवश्यकताओं के लिए सबसे उपयुक्त हो।",
    "form_step_05_title": "चरण 0.5: स्टाम्प ड्यूटी और रजिस्ट्रेशन का भुगतान कौन करेगा?",
    "form_step_05_description": "चुनें कि स्टाम्प ड्यूटी और रजिस्ट्रेशन शुल्क का भुगतान कौन करेगा",
    "form_step_1_title": "चरण 1: मकान मालिक (संपत्ति मालिक) विवरण",
    "form_step_2_title": "चरण 2: किरायेदार विवरण",
    "form_step_3_title": "चरण 3: संपत्ति विवरण",
    "form_step_4_title": "चरण 4: किराये की शर्तें",
    "form_step_5_title": "चरण 5: समझौते की शर्तें और अवधि",
    "form_step_6_title": "चरण 6: शर्तें और शर्तें",
    "form_step_7_title": "चरण 7: क्षेत्राधिकार और रजिस्ट्रेशन",
    "form_step_8_title": "चरण 8: समझौते को साझा करें",

    // Agreement Type Options
    "form_estamped_title": "ई-स्टाम्प समझौता (Orello)",
    "form_estamped_desc": "कानूनी रूप से स्टाम्प किया गया • ₹299 + स्टाम्प ड्यूटी",
    "form_estamped_benefit": "कानूनी रूप से वैध और किसी भी अवधि के लिए न्यायालय-स्वीकार्य",

    "form_eagreement_title": "ई-समझौता (Orello)",
    "form_eagreement_desc": "डिजिटल समझौता • ₹99 फ्लैट",
    "form_eagreement_benefit": "कानूनी रूप से न्यायालय-वैध नहीं। 11 महीने से कम के लिए सर्वश्रेष्ठ।",

    "form_traditional_title": "पारंपरिक कागज (बाहरी)",
    "form_traditional_desc": "आपका अपना समझौता • केवल Orello जमानत सुरक्षा",
    "form_traditional_benefit": "अपना या ब्रोकर-निर्मित समझौता उपयोग करें। Orello केवल जमानत प्रबंधित करता है।",

    // Form Fields - Common
    "form_full_name": "पूरा नाम",
    "form_full_name_placeholder": "पूरा नाम दर्ज करें",
    "form_age": "आयु",
    "form_age_placeholder": "आयु दर्ज करें",
    "form_email": "ईमेल पता",
    "form_email_placeholder": "ईमेल दर्ज करें",
    "form_phone": "फोन नंबर",
    "form_phone_placeholder": "10-अंकीय फोन नंबर",
    "form_address": "संपूर्ण पता",
    "form_address_placeholder": "संपूर्ण पता दर्ज करें",
    "form_pan": "PAN: AAAAA NNNN C",
    "form_pan_placeholder": "उदा। ABCDE1234F",
    "form_fathersname": "पिता/पति का नाम",
    "form_fathersname_placeholder": "नाम दर्ज करें",

    // Property Details
    "form_property_type": "संपत्ति का प्रकार",
    "form_residential": "आवासीय",
    "form_commercial": "व्यावसायिक",
    "form_property_state": "संपत्ति राज्य",
    "form_property_city": "संपत्ति शहर",
    "form_property_address": "संपत्ति का पता",
    "form_property_address_placeholder": "संपूर्ण संपत्ति का पता दर्ज करें",
    "form_property_bedrooms": "BHK कॉन्फ़िगरेशन",
    "form_property_furnished": "फर्नीचर की स्थिति",
    "form_furnished": "पूरी तरह सज्जित",
    "form_semi_furnished": "आधा सज्जित",
    "form_unfurnished": "बिना सज्जित",

    // Rental Terms
    "form_monthly_rent": "मासिक किराया (₹)",
    "form_deposit_amount": "जमानत राशि (₹)",
    "form_lock_in_months": "लॉक-इन अवधि (महीने)",
    "form_start_date": "शुरुआत की तारीख",
    "form_end_date": "समाप्ति तारीख (स्वचालित-गणना)",
    "form_end_date_help": "सिस्टम शुरुआत की तारीख + अवधि के आधार पर स्वचालित रूप से गणना करता है।",
    "form_lease_duration": "पट्टा अवधि (वर्ष)",
    "form_lease_duration_months": "पट्टा अवधि (महीने)",

    // Fee Payer Options
    "form_tenant_pays_full": "किरायेदार पूरी राशि का भुगतान करता है",
    "form_tenant_pays_desc": "किरायेदार जमानत + स्टाम्प ड्यूटी और रजिस्ट्रेशन का भुगतान करता है",
    "form_landlord_pays_full": "मकान मालिक पूरी राशि का भुगतान करता है",
    "form_landlord_pays_desc": "मकान मालिक स्टाम्प ड्यूटी और रजिस्ट्रेशन अलग से वहन करता है",
    "form_split_50": "50-50 विभाजित करें",
    "form_split_50_desc": "किरायेदार और मकान मालिक प्रत्येक आधी स्टाम्प ड्यूटी और रजिस्ट्रेशन का भुगतान करते हैं",

    // Utilities & Maintenance
    "form_utilities": "उपयोगिताएँ और रखरखाव",
    "form_electricity": "बिजली",
    "form_electricity_landlord": "बिजली: मकान मालिक भुगतान करता है",
    "form_electricity_tenant": "बिजली: किरायेदार भुगतान करता है",
    "form_water": "पानी",
    "form_water_landlord": "पानी: मकान मालिक भुगतान करता है",
    "form_water_tenant": "पानी: किरायेदार भुगतान करता है",
    "form_gas": "गैस",
    "form_gas_landlord": "गैस: मकान मालिक भुगतान करता है",
    "form_gas_tenant": "गैस: किरायेदार भुगतान करता है",
    "form_property_tax": "संपत्ति कर",
    "form_property_tax_landlord": "संपत्ति कर: मकान मालिक भुगतान करता है",
    "form_property_tax_tenant": "संपत्ति कर: किरायेदार भुगतान करता है",
    "form_maintenance_charges": "रखरखाव शुल्क (मासिक)",
    "form_maintenance_charges_placeholder": "राशि ₹ में दर्ज करें",

    // Buttons
    "form_back": "← वापस",
    "form_next": "आगे →",
    "form_submit": "समझौता बनाएँ",
    "form_cancel": "रद्द करें",
    "form_download_pdf": "PDF डाउनलोड करें",
    "form_switch_estamp": "⬆️ ई-स्टाम्प पर स्विच करें",

    // Deposit Protection
    "form_deposit_protection": "जमानत सुरक्षा",
    "form_deposit_protection_yes": "हाँ, मैं जमानत सुरक्षा चाहता हूँ",
    "form_deposit_protection_no": "नहीं, जमानत सुरक्षा छोड़ें",

    // Errors & Validation
    "form_error_required": "यह फील्ड आवश्यक है",
    "form_error_invalid_email": "कृपया एक वैध ईमेल पता दर्ज करें",
    "form_error_invalid_phone": "कृपया एक वैध 10-अंकीय फोन नंबर दर्ज करें",
    "form_error_invalid_pan": "कृपया एक वैध PAN दर्ज करें (प्रारूप: AAAAA NNNN C)",
    "form_error_dates": "समाप्ति तारीख शुरुआत की तारीख के बाद होनी चाहिए",
    "form_error_select_option": "आगे बढ़ने से पहले कृपया एक विकल्प चुनें",

    // PDF & Sharing
    "form_pdf_title": "समझौते की पूर्वावलोकन और डाउनलोड",
    "form_pdf_desc_eagreement": "यह सभी कानूनी खंडों और क्षेत्राधिकार-विशिष्ट शर्तों के साथ व्यापक समझौता है।",
    "form_pdf_desc_estamp": "यह सरकारी स्टाम्प ड्यूटी, कानूनी खंडों और क्षेत्राधिकार-विशिष्ट शर्तों के साथ व्यापक ई-स्टाम्प समझौता है।",
    "form_share_agreement": "समझौते को साझा करें",
    "form_send_invite": "आमंत्रण भेजें",
    "form_registration_status": "रजिस्ट्रेशन स्थिति",
    "form_ready_to_sign": "हस्ताक्षर के लिए तैयार",

    // Main Dashboard UI (Hindi)
    "page_create_agreement": "करार बनाएँ",
    "page_fill_details": "करार की विस्तृत जानकारी भरें। लाइव पूर्वावलोकन को वास्तविक समय में अपडेट होते देखें।",
    "page_select_state": "करार टेम्पलेट के लिए राज्य चुनें",
    "page_select_state_desc": "उपयुक्त करार टेम्पलेट लोड करने के लिए अपना राज्य चुनें। विभिन्न राज्यों के लिए पट्टा समझौतों के कानूनी आवश्यकताएं अलग-अलग होती हैं।",
    "page_select_state_label": "📍 अपना राज्य चुनें",
    "page_agreement_format": "📋 करार प्रारूप",
    "page_send_invites": "📧 करार सामायिक करें",
    "page_pdf_preview": "📄 करार पूर्वावलोकन और डाउनलोड",
    "page_registration_status": "पंजीकरण स्थिति",
    "page_landlord": "🏠 संपत्ति मालिक",
    "page_tenant": "👤 किरायेदार",
    "page_ready_to_sign": "हस्ताक्षर के लिए तैयार",
    "page_registered": "✓ पंजीकृत",
    "page_not_registered": "○ पंजीकृत नहीं",

    // Sidebar Navigation (Hindi)
    "sidebar_home": "होम",
    "sidebar_overview": "अवलोकन",
    "sidebar_create_agreement": "करार बनाएँ",
    "sidebar_my_team": "मेरी टीम",
    "sidebar_my_deals": "मेरे डील",
    "sidebar_all_deals": "सभी डील",
    "sidebar_my_earnings": "मेरी कमाई",
    "sidebar_my_rental": "मेरा किराया",
    "sidebar_deposits": "जमानतें",
    "sidebar_wallet": "वॉलेट",
    "sidebar_notifications": "सूचनाएं",
    "sidebar_analytics": "विश्लेषण",
    "sidebar_business_profile": "व्यावसायिक प्रोफाइल",
    "sidebar_referral": "रेफरल",
    "sidebar_profile": "प्रोफाइल",
    "sidebar_settings": "सेटिंग्स",
    "sidebar_support": "सहायता",
    "sidebar_help": "मदद",
  },

  mr: {
    // Navigation
    "nav_for_tenants": "भाडेकरांसाठी",
    "nav_for_landlords": "मालकांसाठी",
    "nav_for_brokers": "ब्रोकर्स आणि एजंटांसाठी",
    "nav_trust": "विश्वास",
    "nav_about": "आमच्याबद्दल",
    "nav_contact": "संपर्क करा",
    "nav_login": "लॉगिन",
    "nav_get_started": "विनामूल्य सुरू करा",

    // Hero & Main
    "no_deposit_fights": "डिपोজिटवर कोणतीही झगडे नाहीत.",
    "renting_without_fights": "भाडेसाठी घेणे, झगड्यांशिवाय.",
    "hero_sub": "Orello करार, डिपोजिट आणि मालमत्ता अहवाल संभाळतो — सर्वांसाठी न्याय.",
    "get_started": "विनामूल्य सुरू करा",
    "see_how": "कसे कार्य करते हे पहा",
    "login": "लॉगिन",
    "sign_up": "साइन अप",
    "create_free_account": "विनामूल्य खाता तयार करा",

    // For Tenants
    "your_deposit_deserves": "आपली डिपोजिट परत येण्यास योग्य आहे.",
    "deposit_protected": "₹45,000+ डिपोजिट सुरक्षित",
    "avg_resolution": "7 दिवस सरासरी समाधान",
    "success_rate": "99.2% विवाद सुटले",
    "deposit_in_escrow": "एस्क्रोमध्ये डिपोजिट",
    "proof_locked": "पहिल्या दिवसी पुरावा लॉक",
    "you_approve": "तुम्ही प्रत्येक कपात मंजूर करा",
    "disputes_resolved": "10 दिवसांत विवाद सुटले",

    // For Landlords
    "rent_with_confidence": "आत्मविश्वासाने भाडेसाठी द्या.",
    "properties_listed": "12,000+ सत्यापित मालमत्ता",
    "avg_deduction": "₹8,500 सरासरी कपात",
    "paperwork_free": "100% डिजिटल प्रक्रिया",
    "verified_tenants": "सत्यापित भाडेकर",
    "court_valid": "न्यायालय-वैध करार",
    "property_documented": "मालमत्ता दस्तऐवज",
    "clean_exits": "स्वच्छ बाहेर पडणे",

    // For Brokers
    "close_more_deals": "अधिक डील बंद करा. कमी झगडे.",
    "brokers_partnered": "8,500+ सक्रिय एजंट",
    "avg_earnings": "₹4,500 प्रति डील",
    "faster_closures": "5 मिनिटे सेटअप ते स्वाक्षर",
    "earn_on_deals": "प्रत्येक डीलवर कमा",

    // Trust & Security
    "we_protect_everyone": "आम्ही सर्वांची रक्षा करतो.",
    "regulated_escrow": "नियंत्रित एस्क्रो",
    "legal_agreements": "कायदेशीर करार",
    "timestamped_records": "टाइमस्टॅम्प केलेले रेकॉर्ड",

    // About
    "renting_broken": "भाडेसाठी घेणे इतके तुटलेले नसावे.",
    "we_started": "आम्ही सुरू केले कारण आम्ही गुस्सात होतो.",
    "fair": "न्याय्य",
    "fast": "वेगवान",
    "transparent": "पारदर्शक",

    // Contact
    "get_in_touch": "आमच्याशी संपर्क साधा",
    "send_message": "आमच्याला संदेश पाठवा",
    "email": "ईमेल",
    "whatsapp": "व्हाट्सएप",
    "office": "कार्यालय",
    "full_name": "पूर्ण नाव",
    "email_address": "ईमेल पता",
    "message": "संदेश",

    // Footer
    "terms": "अटी",
    "privacy": "गोपनीयता",
    "copyright": "© 2025 Orello. सर्व हक्क राखीव.",

    // Common
    "sound_familiar": "परिचित वाटते?",
    "simple_secure_done": "सोपे. सुरक्षित. झाले.",
    "built_for_everyone": "सर्वांसाठी बनवले",
    "why_orello": "Orello का?",
    "simple_pricing": "सोपी किमत",
    "start_today": "आज सुरू करा. विनामूल्य.",
    "tenants": "भाडेकर",
    "landlords": "मालक",
    "brokers": "ब्रोकर्स",
    "made_for_india": "भारतासाठी बनवले",
    "how_it_works": "कसे कार्य करते",
    "language": "भाषा",

    // Hero section
    "hero_built_for": "भारतीय भाड्यासाठी बनवले",
    "hero_cta_get_started": "विनामूल्य सुरू करा",
    "hero_cta_see_how": "कसे कार्य करते हे पहा",
    "hero_ticker_setup": "₹0 सेटअप शुल्क",
    "hero_ticker_deal": "5 मिनिटांत डील तयार करा",
    "hero_ticker_disputes": "10 दिवसांत निकाल",
    "hero_ticker_legal": "100% कायदेशीर करार",

    // Ticker bar
    "ticker_escrow": "एस्क्रोमध्ये डिपोजिट",
    "ticker_legal": "कायदेशीर करार",
    "ticker_aadhaar": "आधार-सत्यापित भाडेकर",
    "ticker_condition": "टाइमस्टॅम्प केलेले अहवाल",
    "ticker_dispute": "न्याय्य वाद निवारण",
    "ticker_nowhatsapp": "व्हाट्सएप भांडणे नाही",
    "ticker_3way": "3-पक्षी सत्यापित डील",
    "ticker_india": "भारतासाठी बनवले",

    // Problem section
    "problem_label": "समस्या",
    "problem_heading": "परिचित वाटते?",
    "problem_01_title": "डिपोजिट नाहीसे झाले",
    "problem_01_body": "₹80,000 दिले. ₹20,000 परत मिळाले. कोणतीही यादी नाही, फोटो नाही, स्पष्टीकरण नाही. फक्त एक आकडा.",
    "problem_01_tag": "प्रत्येक ३ पैकी १ भाडेकराशी होते",
    "problem_02_title": "पुरावा नाही, केस नाही",
    "problem_02_body": "भाडेकर म्हणतो ओरखडा आधीपासून होता. मालक म्हणतो नव्हता. कुणाकडे फोटो नाही. कुणीच जिंकत नाही.",
    "problem_02_tag": "दस्तावेज नाही = निराकरण नाही",
    "problem_03_title": "व्हाट्सएप कायदेशीर कागद नाही",
    "problem_03_body": "प्रत्येक मूव्ह-आउट भांडणांची, स्क्रीनशॉटची आणि शांततेची साखळी बनते. मग कोणीतरी उत्तर देणे बंद करते.",
    "problem_03_tag": "तरीही ब्रोकरला दोष मिळतो",

    // How it works
    "how_label": "Orello चा मार्ग",
    "how_heading_1": "एक भाडे.",
    "how_heading_2": "सुरक्षेचे सहा टप्पे.",
    "how_body": "प्रत्येक भारतीय भाडे एकाच धोकादायक वाटेने जाते. Orello प्रत्येक टप्प्यावर तुमचे रक्षण करते — योग्य जागा शोधण्यापासून डिपोजिट परत मिळवण्यापर्यंत.",
    "how_step_01_title": "तुमचा एजंट शोधा",
    "how_step_01_desc": "Orello वर सत्यापित ब्रोकर्स आणि एजंट शोधा. खऱ्या रेटिंग, खऱ्या डील्स, पारदर्शी कमिशन.",
    "how_step_02_title": "करारावर स्वाक्षरी करा",
    "how_step_02_desc": "ई-स्टॅम्प केलेला, न्यायालय-वैध करार. सर्व पक्ष डिजिटली स्वाक्षरी करतात. नोटरी नाही, प्रिंटर नाही.",
    "how_step_03_title": "डिपोजिट एस्क्रोमध्ये सुरक्षित",
    "how_step_03_desc": "भाडेकर पैसे देतो — पैसे Orello कडे राहतात, मालकाकडे नाही. दोन्ही बाजूंच्या सहमतीशिवाय लॉक्ड.",
    "how_step_04_title": "मूव्ह-इन रिपोर्ट लॉक",
    "how_step_04_desc": "भिंती, मजले, फिक्स्चर — फोटो, टाइमस्टॅम्प, दोन्ही बाजूंची सहमती. नंतर 'आधीपासून असे होते' असे बोलण्याची संधी नाही.",
    "how_step_05_title": "निश्चिंत भाडेकारी",
    "how_step_05_desc": "Orello द्वारे भाडे भरा. सर्व काही एका ठिकाणी ट्रॅक करा. नूतनीकरण स्मरणपत्रे, दुरुस्ती विनंत्या — सर्व एका फीडमध्ये.",
    "how_step_06_title": "नाटकाशिवाय निपटारा",
    "how_step_06_desc": "मूव्ह-आउट रिपोर्टची मूव्ह-इनशी तुलना. खऱ्या कपाती मिनिटांत ठरवल्या. वाद? Orello निर्णय देतो — तटस्थ, जलद, अंतिम.",
    "how_bottom_pill": "प्रत्येक पाऊल डिजिटल, टाइमस्टॅम्पड आणि कायदेशीर बंधनकारक आहे.",

    // Why Orello
    "why_label": "Orello का",
    "why_heading_1": "भारतीय खऱ्या",
    "why_heading_2": "भाड्यासाठी बनवले.",
    "why_b1_title": "पैसे सुरक्षित लॉक",
    "why_b1_desc": "तुमचे डिपोजिट Orello च्या नियंत्रित एस्क्रोमध्ये राहते. दोन्ही बाजूंच्या सहमतीनेच सुटते.",
    "why_b2_title": "१५ मिनिटांत सेटअप",
    "why_b2_desc": "करार, KYC, डिपोजिट, स्थिती अहवाल — सर्व एकाच बैठकीत फोनवरून.",
    "why_b3_title": "न्याय्य वाद निवारण",
    "why_b3_desc": "दोन्ही बाजू पुरावे सादर करतात. Orello पुराव्यावर निर्णय देतो — चहापाणीत केलेला तडजोड नाही.",
    "why_b4_title": "न्यायालय-वैध करार",
    "why_b4_desc": "भारतीय भाडे कायद्यांतर्गत ई-स्टॅम्प. प्रिंटर किंवा नोटरीशिवाय न्यायालयात ग्राह्य.",
    "why_b5_title": "न फिकट होणारा पुरावा",
    "why_b5_desc": "मूव्ह-इनचे फोटो आणि नोट्स पहिल्या दिवशी टाइमस्टॅम्पड. मूव्ह-आउट तुलना आपोआप.",
    "why_b6_title": "₹0 दलाली घासाघीस",
    "why_b6_desc": "पारदर्शी कमिशन, आधीच दर्शवलेले. लपलेल्या फी नाहीत, दलाल नाटक नाही.",

    // Persona: Tenants
    "persona_tenant_label": "भाडेकरांसाठी",
    "persona_tenant_headline": "तुमचे डिपोजिट\nतुमचेच आहे.",
    "persona_tenant_body": "मालक महिने तुमचे पैसे रोखतात आणि त्यांच्या अटींवर परत करतात. Orello ते एस्क्रोमध्ये ठेवते — लॉक्ड, टाइमस्टॅम्पड, आणि तुमच्या सहमतीनेच सुटते.",
    "persona_tenant_f1_t": "एस्क्रोमध्ये डिपोजिट",
    "persona_tenant_f1_d": "तुमचे पैसे Orello कडे सुरक्षित आहेत — मालकाच्या खात्यात नाही. तुमच्या परवानगीशिवाय ते स्पर्श करू शकत नाहीत.",
    "persona_tenant_f2_t": "पहिल्या दिवशी पुरावा लॉक",
    "persona_tenant_f2_d": "फोटो, व्हिडिओ आणि स्थिती नोट्स मूव्ह-इनला टाइमस्टॅम्पसह सील. नंतर कोणी 'असेच होते' म्हणू शकत नाही.",
    "persona_tenant_f3_t": "प्रत्येक कपात तुम्ही मंजूर करा",
    "persona_tenant_f3_d": "एक पैसाही हलण्यापूर्वी प्रत्येक दावा पहा. जे पटत नाही त्याला आव्हान द्या.",
    "persona_tenant_f4_t": "१० दिवसांत वाद सुटतात",
    "persona_tenant_f4_d": "सहमती न झाल्यास Orello पुरावे पाहून १० दिवसांत बंधनकारक निर्णय देते. वकील नाही, न्यायालय नाही.",
    "persona_tenant_cta": "भाडेकर म्हणून साइन अप करा",

    // Persona: Landlords
    "persona_landlord_label": "मालकांसाठी",
    "persona_landlord_headline": "तुमची मालमत्ता.\nतुमच्या अटी.\nकोणतीही आश्चर्ये नाहीत.",
    "persona_landlord_body": "बहुतेक मालकांचे नुकसान वाईट भाडेकरांमुळे नव्हे, तर वाईट दस्तावेजीकरणामुळे होते. Orello तुम्हाला सत्यापित भाडेकर, न्यायालय-वैध करार आणि किल्ली देण्यापूर्वी खोली-खोलीचा पुरावा देते.",
    "persona_landlord_f1_t": "कोण येतंय ते जाणा",
    "persona_landlord_f1_d": "प्रत्येक भाडेकराची किल्ली मिळण्यापूर्वी आधार-KYC केली जाते. फोटो नाही, फॉरवर्ड ID नाही — खरी KYC.",
    "persona_landlord_f2_t": "न्यायालयात टिकणारे करार",
    "persona_landlord_f2_d": "ई-स्टॅम्पड, डिजिटली स्वाक्षरी केलेला भाडे करार. भारतात कुठेही वैध. कधीही डाउनलोड करा. नोटरी नाही.",
    "persona_landlord_f3_t": "मूव्ह-इनपूर्वी फ्लॅट नोंद",
    "persona_landlord_f3_d": "फोटोंसह खोली-खोलीचा स्थिती अहवाल, मूव्ह-इनला लॉक. मूव्ह-आउटला नुकसानीचा अखंडनीय पुरावा.",
    "persona_landlord_f4_t": "प्रत्येकवेळी स्वच्छ निर्गमन",
    "persona_landlord_f4_d": "दोन्ही बाजू प्लेटफॉर्मवर कपाती ठरवतात. अंतिम दिवशी भांडणे नाहीत, व्हाट्सएपवर पाठपुरावा नाही.",
    "persona_landlord_cta": "मालक म्हणून साइन अप करा",

    // Persona: Brokers
    "persona_broker_label": "ब्रोकर्स आणि एजंटांसाठी",
    "persona_broker_headline": "अधिक डील.\nकमी भांडणे.",
    "persona_broker_body": "तुम्ही अर्धा वेळ कागदपत्रे करण्यात आणि अर्धा डिपोजिट वाद सोडवण्यात घालवता. Orello दोन्ही सांभाळते — तुम्ही डील बंद करण्यावर लक्ष द्या.",
    "persona_broker_f1_t": "मिनिटांत करार",
    "persona_broker_f1_d": "एकाच फ्लोमध्ये कायदेशीर, ई-स्टॅम्पड भाडे करार तयार करा. KYC, डिपोजिट, स्वाक्षऱ्या — क्लायंट जाण्यापूर्वी.",
    "persona_broker_f2_t": "सगळे, कुठेही पहा",
    "persona_broker_f2_d": "सर्व करार, अहवाल आणि डील स्टेटस एका डॅशबोर्डमध्ये — कोणत्याही डिव्हाइसवर, कधीही.",
    "persona_broker_f3_t": "मध्यस्थ होणे बंद करा",
    "persona_broker_f3_d": "वाद Orello कडे जातात, तुमच्या फोनवर नाही. भाडेकर आणि मालक प्लेटफॉर्मवर सोडवतात.",
    "persona_broker_f4_t": "बंद करा, कमवा",
    "persona_broker_f4_d": "प्रत्येक डिपोजिट संरक्षणावर १५% कमिशन. आपोआप ट्रॅक, हवे तेव्हा काढा.",
    "persona_broker_cta": "ब्रोकर म्हणून साइन अप करा",

    // Proof section
    "proof_label": "पुरावा",
    "proof_heading_1": "पुरावा",
    "proof_heading_2": "स्वतःच बोलतो.",
    "proof_stat1_label": "सुरू करण्यासाठी",
    "proof_stat1_sub": "सर्वांसाठी मोफत",
    "proof_stat2_label": "डील तयार करण्यासाठी",
    "proof_stat2_sub": "KYC ते स्वाक्षरी",
    "proof_stat3_label": "वाद निवारण",
    "proof_stat3_sub": "बंधनकारक निर्णय",
    "proof_stat4_label": "कायदेशीर वैध",
    "proof_stat4_sub": "ई-स्टॅम्पड आणि स्वाक्षरी",
    "proof_stat5_label": "पुरावा लॉक",
    "proof_stat5_sub": "कायमचे टाइमस्टॅम्पड",

    // FOMO / Social proof
    "fomo_label": "संपूर्ण भारतातील भाडेकर आणि मालकांचा विश्वास",
    "fomo_heading": "खरे लोक. खरे डिपोजिट.\nखरे निराकरण.",
    "fomo_stat1_label": "भाडेकरांना पूर्ण डिपोजिट परत मिळाले",
    "fomo_stat2_label": "मालकांनी वादाशिवाय भाडेकारी संपवली",
    "fomo_stat3_label": "वाद १० दिवसांपेक्षा कमी वेळात सुटले",
    "fomo_stat4_label": "ब्रोकर जलद डील बंद करत आहेत",
    "review_1_quote": "मला खात्री होती भिंतीवरील ओरखड्यामुळे माझे डिपोजिट जाईल. Orello च्या मूव्ह-इन फोटोंनी मला ₹45,000 वाचवले.",
    "review_1_role": "भाडेकर",
    "review_1_location": "वांद्रे, मुंबई",
    "review_2_quote": "शेवटचा भाडेकर न सांगता निघून गेला. Orello मुळे माझ्याकडे स्वाक्षरी केलेला करार, नोंद केलेली स्थिती होती आणि ८ दिवसांत हिशेब झाला.",
    "review_2_role": "मालक",
    "review_2_location": "कोथरूड, पुणे",
    "review_3_quote": "मी महिन्याला ६–८ डील बंद करतो. Orello प्रत्येक एक स्वच्छ बनवते — व्हाट्सएप भांडण नाही, स्वाक्षऱ्यांसाठी पाठपुरावा नाही.",
    "review_3_role": "ब्रोकर",
    "review_3_location": "अंधेरी, मुंबई",
    "review_4_quote": "मालकाने रंगकामासाठी ₹30,000 कापण्याचा प्रयत्न केला. Orello ने दाखवले मूव्ह-इनला भिंती ठीक होत्या. प्रत्येक रुपया परत मिळाला.",
    "review_4_role": "भाडेकर",
    "review_4_location": "कोरमंगला, बंगळुरू",
    "review_5_quote": "शेवटी दोन्ही बाजूंचे रक्षण करणारे एक व्यासपीठ. माझ्या भाडेकराला डिपोजिट देताना आणि मला घेताना सुरक्षितता वाटली.",
    "review_5_role": "मालक",
    "review_5_location": "अड्यार, चेन्नई",
    "review_6_quote": "गेल्या महिन्यात ३ ब्रोकर रेफर केले, ₹750 बोनस मिळाला. आणि ग्राहक परत येतात कारण ते प्रक्रियेवर विश्वास ठेवतात.",
    "review_6_role": "एजंट",
    "review_6_location": "जुबिली हिल्स, हैदराबाद",

    // Broker CTA
    "broker_cta_label": "ब्रोकर्स आणि एजंटांसाठी",
    "broker_cta_heading_1": "तुमचे मालमत्ता व्यवस्थापन व्यासपीठ.",
    "broker_cta_heading_2": "सुरुवात मोफत.",
    "broker_cta_body": "करार, KYC, डिपोजिट, स्थिती अहवाल, वाद निवारण — एका डॅशबोर्डमधून भाड्याच्या प्रत्येक टप्प्यासाठी.",
    "broker_cta_btn": "तुमचे मोफत खाते तयार करा",
    "broker_cta_fine": "क्रेडिट कार्ड नाही. सेटअप शुल्क नाही. असीमित करार.",

    // Final CTA
    "final_cta_heading_1": "तुमचे पुढचे भाडे.",
    "final_cta_heading_2": "सुरक्षित.",
    "final_cta_body": "क्रेडिट कार्ड नाही. सेटअप शुल्क नाही. मिनिटांत तयार.",
    "final_cta_btn": "मोफत खाते तयार करा",

    // Login page
    "login_welcome": "परत स्वागत आहे",
    "login_subtitle_default": "तुमच्या फोन किंवा ईमेल आणि पासवर्डने साइन इन करा.",
    "login_subtitle_tenant": "तुमची ठेव रक्कम सुरक्षित ठेवण्यासाठी साइन इन करा",
    "login_subtitle_landlord": "तुमच्या भाडेकरूची पडताळणी करण्यासाठी साइन इन करा",
    "login_subtitle_broker": "प्रत्येक डीलवर कमिशन मिळवण्यासाठी साइन इन करा",
    "login_phone_label": "फोन किंवा ईमेल",
    "login_phone_placeholder": "9876543210 किंवा you@example.com",
    "login_password_label": "पासवर्ड",
    "login_forgot": "पासवर्ड विसरलात?",
    "login_btn": "साइन इन करा",
    "login_btn_loading": "साइन इन होत आहे…",
    "login_no_account": "खाते नाही?",
    "login_signup": "साइन अप करा",

    // Agreement Form Builder (Marathi)
    "form_step_0_title": "चरण 0: आपले करार प्रकार निवडा",
    "form_step_0_description": "आपल्या गरजांनुसार योग्य करार प्रारूप निवडा।",
    "form_step_1_title": "चरण 1: मालक विवरण",
    "form_step_2_title": "चरण 2: भाडेकर विवरण",
    "form_step_4_title": "चरण 4: भाडे अटी",
    "form_step_5_title": "चरण 5: करार मुदत आणि शेवटची तारीख",
    "form_step_8_title": "चरण 8: करार सामायिक करा",
    "form_estamped_title": "ई-स्टाम्प करार",
    "form_eagreement_title": "ई-करार",
    "form_full_name": "पूर्ण नाव",
    "form_start_date": "प्रारंभ तारीख",
    "form_end_date": "शेवटची तारीख",
    "form_back": "← मागे",
    "form_next": "पुढे →",
    "form_submit": "करार तयार करा",

    // Main Dashboard UI (Marathi)
    "page_create_agreement": "करार तयार करा",
    "page_fill_details": "करारामध्ये तपशील भरा। लाइव पूर्वावलोकन रिअल-टाइममध्ये अपडेट होते पहा।",
    "page_select_state": "करार टेम्पलेटसाठी राज्य निवडा",
    "page_select_state_desc": "योग्य करार टेम्पलेट लोड करण्यासाठी आपल्या राज्याचा निवड करा। विविध राज्यांमध्ये पट्ट्यांच्या समझौतेच्या कानूनी आवश्यकता वेगवेगळ्या आहेत।",
    "page_select_state_label": "📍 आपली राज्य निवडा",
    "page_agreement_format": "📋 करार प्रकार",
    "page_send_invites": "📧 करार सामायिक करा",
    "page_pdf_preview": "📄 करार पूर्वावलोकन आणि डाउनलोड",
    "page_registration_status": "नोंदणी स्थिती",
    "page_landlord": "🏠 मकान मालिक",
    "page_tenant": "👤 भाडेकर",
    "page_ready_to_sign": "हस्ताक्षरासाठी तयार",
    "page_registered": "✓ नोंदणीकृत",
    "page_not_registered": "○ नोंदणीकृत नाही",

    // Sidebar Navigation (Marathi)
    "sidebar_home": "होम",
    "sidebar_overview": "आढावा",
    "sidebar_create_agreement": "करार तयार करा",
    "sidebar_my_team": "माझी टीम",
    "sidebar_my_deals": "माझे डिल",
    "sidebar_all_deals": "सर्व डिल",
    "sidebar_my_earnings": "माझी कमाई",
    "sidebar_my_rental": "माझे भाडे",
    "sidebar_deposits": "ठेव",
    "sidebar_wallet": "वॉलेट",
    "sidebar_notifications": "सूचना",
    "sidebar_analytics": "विश्लेषण",
    "sidebar_business_profile": "व्यावसायिक प्रोफाइल",
    "sidebar_referral": "संदर्भ",
    "sidebar_profile": "प्रोफाइल",
    "sidebar_settings": "सेटिंग्स",
    "sidebar_support": "समर्थन",
    "sidebar_help": "मदत",
  },

  kn: {
    // Navigation
    "nav_for_tenants": "ಬಾಡಿಗೆದಾರರಿಗೆ",
    "nav_for_landlords": "ಮಾಲಿಕರಿಗೆ",
    "nav_for_brokers": "ಬ್ರೋಕರ್‌ಗಳು ಮತ್ತು ಏಜೆಂಟ್‌ಗಳಿಗೆ",
    "nav_trust": "ವಿಶ್ವಾಸ",
    "nav_about": "ನಮ್ಮ ಬಗ್ಗೆ",
    "nav_contact": "ಸಂಪರ್ಕಿಸಿ",
    "nav_login": "ಲಾಗಿನ್",
    "nav_get_started": "ಉಚಿತವಾಗಿ ಪ್ರಾರಂಭಿಸಿ",

    // Hero & Main
    "no_deposit_fights": "ಠೇವಣೆ ಬಗ್ಗೆ ಯಾವುದೇ ಝಗಡೆ ಇಲ್ಲ.",
    "renting_without_fights": "ಬಾಡಿಗೆ, ಝಗಡೆಗಳಿಲ್ಲದೆ.",
    "hero_sub": "Orello ಒಪ್ಪಂದಗಳು, ಠೇವಣೆಗಳು ಮತ್ತು ಆಸ್ತಿ ವರದಿಗಳನ್ನು ನಿರ್ವಹಿಸುತ್ತದೆ — ಎಲ್ಲರಿಗೆ ನ್ಯಾಯ.",
    "get_started": "ಉಚಿತವಾಗಿ ಪ್ರಾರಂಭಿಸಿ",
    "see_how": "ಇದು ಹೇಗೆ ಕೆಲಸ ಮಾಡುತ್ತದೆ ಎಂಬುದನ್ನು ನೋಡಿ",
    "login": "ಲಾಗಿನ್",
    "sign_up": "ಸೈನ್ ಅಪ್",
    "create_free_account": "ಉಚಿತ ಖಾತೆ ರಚಿಸಿ",

    // For Tenants
    "your_deposit_deserves": "ನಿಮ್ಮ ಠೇವಣೆ ಹಿಂತಿರುಗಿ ಬರುವಿಕೆಗೆ ಯೋಗ್ಯ.",
    "deposit_protected": "₹45,000+ ಠೇವಣೆ ಸುರಕ್ಷಿತ",
    "avg_resolution": "7 ದಿನ ಸರಾಸರಿ ಮೃದುಕರಣ",
    "success_rate": "99.2% ವಿವಾದ ಪರಿಹಾರ",
    "deposit_in_escrow": "ಎಸ್ಕ್ರೋನಲ್ಲಿ ಠೇವಣೆ",
    "proof_locked": "ಮೊದಲ ದಿನ ಪುರಾವೆ ಲಾಕ್",
    "you_approve": "ನೀವು ಪ್ರತಿಯೊಂದು ಛಾಯಾಂಕನಕ್ಕೆ ಅನುಮತಿ ಕೊಡಿ",
    "disputes_resolved": "10 ದಿನಗಳಲ್ಲಿ ವಿವಾದ ಪರಿಹಾರ",

    // For Landlords
    "rent_with_confidence": "ಆತ್ಮವಿಶ್ವಾಸದೊಂದಿಗೆ ಬಾಡಿಗೆ ಕೊಡಿ.",
    "properties_listed": "12,000+ ಪರಿಶೀಲಿತ ಆಸ್ತಿಗಳು",
    "avg_deduction": "₹8,500 ಸರಾಸರಿ ಛಾಯಾಂಕನ",
    "paperwork_free": "100% ಡಿಜಿಟಲ್ ಪ್ರಕ್ರಿಯೆ",
    "verified_tenants": "ಪರಿಶೀಲಿತ ಬಾಡಿಗೆದಾರರು",
    "court_valid": "ನ್ಯಾಯಾಲಯ-ಮಾನ್ಯ ಒಪ್ಪಂದಗಳು",
    "property_documented": "ಆಸ್ತಿ ದಾಖಲೆ",
    "clean_exits": "ಸ್ವಚ್ಛ ಪ್ರಸ್ಥಾನ",

    // For Brokers
    "close_more_deals": "ಹೆಚ್ಚು ಡಿಲ್ ಮುಚ್ಚಿ. ಕಡಿಮೆ ಝಗಡೆಗಳು.",
    "brokers_partnered": "8,500+ ಸಕ್ರಿಯ ಏಜೆಂಟ್‌ಗಳು",
    "avg_earnings": "₹4,500 ಪ್ರತಿ ಡಿಲ್",
    "faster_closures": "5 ನಿಮಿಷ ಸೆಟ್‌ಅಪ್ ಠೇವಣೆಗೆ",
    "earn_on_deals": "ಪ್ರತಿಯೊಂದು ಡಿಲ್‌ನಲ್ಲಿ ಸಂಪಾದನೆ ಮಾಡಿ",

    // Trust & Security
    "we_protect_everyone": "ನಾವು ಸೆರೆಯನ್ನು ಎಲ್ಲರನ್ನು ರಕ್ಷಿಸುತ್ತೇವೆ.",
    "regulated_escrow": "ನಿಯಂತ್ರಿತ ಎಸ್ಕ್ರೋ",
    "legal_agreements": "ಕಾನೂನು ಒಪ್ಪಂದಗಳು",
    "timestamped_records": "ಟೈಮ್‌ಸ್ಟ್ಯಾಂಪ್ಡ್ ದಾಖಲೆಗಳು",

    // About
    "renting_broken": "ಬಾಡಿಗೆ ತಕ್ಕೋ ಭಗ್ನವಾಗಿರಬಾರದು.",
    "we_started": "ನಾವು ಕ್ರೋಧದಿಂದ ಪ್ರಾರಂಭಿಸಿದ್ದೇವೆ.",
    "fair": "ನ್ಯಾಯೋಚಿತ",
    "fast": "ವೇಗವಾನ",
    "transparent": "ಪಾರದರ್ಶಕ",

    // Contact
    "get_in_touch": "ನಮ್ಮನ್ನು ಸಂಪರ್ಕಿಸಿ",
    "send_message": "ನಮಗೆ ಸಂದೇಶ ಕಳುಹಿಸಿ",
    "email": "ಈ-ಮೇಲ್",
    "whatsapp": "ವಾಟ್ಸ್‌ಆ್ಯಾಪ್",
    "office": "ಕಾರ್ಯಾಲಯ",
    "full_name": "ಪೂರ್ಣ ನಾಮ",
    "email_address": "ಈ-ಮೇಲ್ ವಿಳಾಸ",
    "message": "ಸಂದೇಶ",

    // Footer
    "terms": "ನಿಯಮಗಳು",
    "privacy": "ಗೋಪನೀಯತೆ",
    "copyright": "© 2025 Orello. ಎಲ್ಲ ಹಕ್ಕುಗಳು ಸಂರಕ್ಷಿತ.",

    // Common
    "sound_familiar": "ಪರಿಚಿತವೆಂದು ತೋರುತ್ತದೆಯೇ?",
    "simple_secure_done": "ಸರಳ. ಸುರಕ್ಷಿತ. ಮುಗಿದೆ.",
    "built_for_everyone": "ಎಲ್ಲರಿಗೆ ನಿರ್ಮಿತವಾಗಿದೆ",
    "why_orello": "Orello ಏಕೆ?",
    "simple_pricing": "ಸರಳ ಬೆಲೆ",
    "start_today": "ಇಂದೇ ಪ್ರಾರಂಭಿಸಿ. ಉಚಿತ.",
    "tenants": "ಬಾಡಿಗೆದಾರರು",
    "landlords": "ಮಾಲಿಕರು",
    "brokers": "ಬ್ರೋಕರ್‌ಗಳು",
    "made_for_india": "ಭಾರತಕ್ಕಾಗಿ ನಿರ್ಮಿತವಾಗಿದೆ",
    "how_it_works": "ಇದು ಹೇಗೆ ಕೆಲಸ ಮಾಡುತ್ತದೆ",
    "language": "ಭಾಷೆ",

    // Hero section
    "hero_built_for": "ಭಾರತೀಯ ಬಾಡಿಗೆಗಾಗಿ ನಿರ್ಮಿತ",
    "hero_cta_get_started": "ಉಚಿತವಾಗಿ ಪ್ರಾರಂಭಿಸಿ",
    "hero_cta_see_how": "ಹೇಗೆ ಕೆಲಸ ಮಾಡುತ್ತದೆ ನೋಡಿ",
    "hero_ticker_setup": "₹0 ಸೆಟ್‌ಅಪ್ ಶುಲ್ಕ",
    "hero_ticker_deal": "5 ನಿಮಿಷದಲ್ಲಿ ಡೀಲ್ ರಚಿಸಿ",
    "hero_ticker_disputes": "10 ದಿನದಲ್ಲಿ ಇತ್ಯರ್ಥ",
    "hero_ticker_legal": "100% ಕಾನೂನುಬದ್ಧ ಒಪ್ಪಂದಗಳು",

    // Ticker bar
    "ticker_escrow": "ಎಸ್ಕ್ರೋನಲ್ಲಿ ಠೇವಣೆ",
    "ticker_legal": "ಕಾನೂನು ಒಪ್ಪಂದಗಳು",
    "ticker_aadhaar": "ಆಧಾರ್-ಪರಿಶೀಲಿತ ಬಾಡಿಗೆದಾರರು",
    "ticker_condition": "ಟೈಮ್‌ಸ್ಟ್ಯಾಂಪ್ ಸ್ಥಿತಿ ವರದಿಗಳು",
    "ticker_dispute": "ನ್ಯಾಯೋಚಿತ ವಿವಾದ ಪರಿಹಾರ",
    "ticker_nowhatsapp": "ವಾಟ್ಸ್‌ಆ್ಯಾಪ್ ಜಗಳಗಳಿಲ್ಲ",
    "ticker_3way": "3-ಮಾರ್ಗ ಪರಿಶೀಲಿತ ಡೀಲ್‌ಗಳು",
    "ticker_india": "ಭಾರತಕ್ಕಾಗಿ ನಿರ್ಮಿತ",

    // Problem section
    "problem_label": "ಸಮಸ್ಯೆ",
    "problem_heading": "ಪರಿಚಿತವಾಗಿ ಅನಿಸುತ್ತಿದೆಯೇ?",
    "problem_01_title": "ಠೇವಣೆ ಮಾಯವಾಯಿತು",
    "problem_01_body": "₹80,000 ಕೊಟ್ಟೆ. ₹20,000 ಹಿಂದಿರುಗಿ ಬಂತು. ಯಾವ ಪಟ್ಟಿಯಿಲ್ಲ, ಫೋಟೋ ಇಲ್ಲ, ವಿವರಣೆ ಇಲ್ಲ. ಕೇವಲ ಒಂದು ಸಂಖ್ಯೆ.",
    "problem_01_tag": "3 ಪೈಕಿ 1 ಬಾಡಿಗೆದಾರರಿಗೆ ಆಗುತ್ತದೆ",
    "problem_02_title": "ಪುರಾವೆ ಇಲ್ಲ, ಕೇಸ್ ಇಲ್ಲ",
    "problem_02_body": "ಬಾಡಿಗೆದಾರ ಗೀರು ಮೊದಲೇ ಇತ್ತು ಎನ್ನುತ್ತಾನೆ. ಮಾಲಿಕ ಇರಲಿಲ್ಲ ಎನ್ನುತ್ತಾನೆ. ಯಾರ ಬಳಿಯೂ ಫೋಟೋ ಇಲ್ಲ. ಯಾರೂ ಗೆಲ್ಲುವುದಿಲ್ಲ.",
    "problem_02_tag": "ದಾಖಲೆ ಇಲ್ಲ = ಪರಿಹಾರ ಇಲ್ಲ",
    "problem_03_title": "ವಾಟ್ಸ್‌ಆ್ಯಾಪ್ ಕಾನೂನು ದಾಖಲೆ ಅಲ್ಲ",
    "problem_03_body": "ಪ್ರತಿ ಮೂವ್-ಔಟ್ ವಾದ-ಸ್ಕ್ರೀನ್‌ಶಾಟ್-ಮೌನದ ಸರಪಳಿಯಾಗುತ್ತದೆ. ನಂತರ ಯಾರೋ ಉತ್ತರ ನೀಡುವುದನ್ನು ನಿಲ್ಲಿಸುತ್ತಾರೆ.",
    "problem_03_tag": "ಹೇಗಿದ್ದರೂ ಬ್ರೋಕರ್‌ಗೆ ದೂಷಣೆ ಸಿಗುತ್ತದೆ",

    // How it works
    "how_label": "Orello ಮಾರ್ಗ",
    "how_heading_1": "ಒಂದು ಬಾಡಿಗೆ.",
    "how_heading_2": "ರಕ್ಷಣೆಯ ಆರು ಹಂತಗಳು.",
    "how_body": "ಪ್ರತಿ ಭಾರತೀಯ ಬಾಡಿಗೆ ಒಂದೇ ಅಪಾಯಕರ ಮಾರ್ಗ ತೆಗೆದುಕೊಳ್ಳುತ್ತದೆ. Orello ಪ್ರತಿ ಹಂತದಲ್ಲೂ ನಿಮ್ಮನ್ನು ರಕ್ಷಿಸುತ್ತದೆ — ಸರಿಯಾದ ಸ್ಥಳ ಹುಡುಕುವುದರಿಂದ ಠೇವಣೆ ಹಿಂತಿರುಗಿಸಿಕೊಳ್ಳುವವರೆಗೆ.",
    "how_step_01_title": "ನಿಮ್ಮ ಏಜೆಂಟ್ ಹುಡುಕಿ",
    "how_step_01_desc": "Orello ನಲ್ಲಿ ಪರಿಶೀಲಿತ ಬ್ರೋಕರ್‌ಗಳು ಮತ್ತು ಏಜೆಂಟ್‌ಗಳನ್ನು ನೋಡಿ. ನಿಜವಾದ ರೇಟಿಂಗ್‌ಗಳು, ನಿಜವಾದ ಡೀಲ್‌ಗಳು, ಪಾರದರ್ಶಕ ಕಮಿಷನ್.",
    "how_step_02_title": "ಒಪ್ಪಂದಕ್ಕೆ ಸಹಿ ಮಾಡಿ",
    "how_step_02_desc": "ಇ-ಸ್ಟ್ಯಾಂಪ್ ಮಾಡಿದ, ನ್ಯಾಯಾಲಯ-ಮಾನ್ಯ ಬಾಡಿಗೆ ಒಪ್ಪಂದ. ಎಲ್ಲ ಪಕ್ಷಗಳು ಡಿಜಿಟಲ್ ಆಗಿ ಸಹಿ ಮಾಡುತ್ತಾರೆ. ನೋಟರಿ ಇಲ್ಲ, ಪ್ರಿಂಟರ್ ಇಲ್ಲ.",
    "how_step_03_title": "ಠೇವಣೆ ಎಸ್ಕ್ರೋದಲ್ಲಿ ಸುರಕ್ಷಿತ",
    "how_step_03_desc": "ಬಾಡಿಗೆದಾರ ಪಾವತಿಸುತ್ತಾರೆ — ಹಣ Orello ಬಳಿ ಇರುತ್ತದೆ, ಮಾಲಿಕರ ಬಳಿ ಅಲ್ಲ. ಇಬ್ಬರ ಒಪ್ಪಿಗೆ ಇಲ್ಲದೆ ಲಾಕ್ ಆಗಿರುತ್ತದೆ.",
    "how_step_04_title": "ಮೂವ್-ಇನ್ ವರದಿ ಲಾಕ್",
    "how_step_04_desc": "ಗೋಡೆಗಳು, ನೆಲ, ಫಿಕ್ಸ್ಚರ್‌ಗಳು — ಫೋಟೋ, ಟೈಮ್‌ಸ್ಟ್ಯಾಂಪ್, ಇಬ್ಬರ ಒಪ್ಪಿಗೆ. ನಂತರ 'ಮೊದಲೇ ಹೀಗಿತ್ತು' ಎಂಬ ವಾದ ಇಲ್ಲ.",
    "how_step_05_title": "ನಿರ್ಭಯ ಬಾಡಿಗೆ ವಾಸ",
    "how_step_05_desc": "Orello ಮೂಲಕ ಬಾಡಿಗೆ ಪಾವತಿಸಿ. ಎಲ್ಲವನ್ನೂ ಒಂದೇ ಜಾಗದಲ್ಲಿ ಟ್ರ್ಯಾಕ್ ಮಾಡಿ. ನವೀಕರಣ ಜ್ಞಾಪನೆಗಳು, ದುರಸ್ತಿ ವಿನಂತಿಗಳು — ಎಲ್ಲ ಒಂದೇ ಫೀಡ್‌ನಲ್ಲಿ.",
    "how_step_06_title": "ನಾಟಕ ಇಲ್ಲದೆ ಇತ್ಯರ್ಥ",
    "how_step_06_desc": "ಮೂವ್-ಔಟ್ ವರದಿಯನ್ನು ಮೂವ್-ಇನ್‌ನೊಂದಿಗೆ ಹೋಲಿಸಿ. ನಿಜವಾದ ಕಡಿತಗಳು ನಿಮಿಷಗಳಲ್ಲಿ ನಿರ್ಧಾರ. ವಿವಾದ? Orello ತೀರ್ಪು ನೀಡುತ್ತದೆ — ತಟಸ್ಥ, ವೇಗ, ಅಂತಿಮ.",
    "how_bottom_pill": "ಪ್ರತಿ ಹಂತ ಡಿಜಿಟಲ್, ಟೈಮ್‌ಸ್ಟ್ಯಾಂಪ್ ಮಾಡಿದ ಮತ್ತು ಕಾನೂನುಬದ್ಧ ಬದ್ಧತೆಯದು.",

    // Why Orello
    "why_label": "Orello ಏಕೆ",
    "why_heading_1": "ಭಾರತೀಯರು ನಿಜವಾಗಿ",
    "why_heading_2": "ಬಾಡಿಗೆ ಮಾಡುವ ರೀತಿಗಾಗಿ ನಿರ್ಮಿತ.",
    "why_b1_title": "ಹಣ ಸುರಕ್ಷಿತ ಲಾಕ್",
    "why_b1_desc": "ನಿಮ್ಮ ಠೇವಣೆ Orello ನ ನಿಯಂತ್ರಿತ ಎಸ್ಕ್ರೋದಲ್ಲಿ ಇರುತ್ತದೆ. ಇಬ್ಬರ ಒಪ್ಪಿಗೆ ಇದ್ದಾಗ ಮಾತ್ರ ಬಿಡುಗಡೆ.",
    "why_b2_title": "15 ನಿಮಿಷದಲ್ಲಿ ಸೆಟ್‌ಅಪ್",
    "why_b2_desc": "ಒಪ್ಪಂದ, KYC, ಠೇವಣೆ, ಸ್ಥಿತಿ ವರದಿ — ನಿಮ್ಮ ಫೋನ್‌ನಿಂದ ಒಂದೇ ಕೂಟದಲ್ಲಿ.",
    "why_b3_title": "ನ್ಯಾಯೋಚಿತ ವಿವಾದ ಪರಿಹಾರ",
    "why_b3_desc": "ಇಬ್ಬರು ಪುರಾವೆ ಸಲ್ಲಿಸುತ್ತಾರೆ. Orello ಸಾಕ್ಷ್ಯಾಧಾರದ ಮೇಲೆ ನಿರ್ಧರಿಸುತ್ತದೆ — ಅನೌಪಚಾರಿಕ ಮಾತುಕತೆ ಅಲ್ಲ.",
    "why_b4_title": "ನ್ಯಾಯಾಲಯ-ಮಾನ್ಯ ಒಪ್ಪಂದಗಳು",
    "why_b4_desc": "ಭಾರತೀಯ ಬಾಡಿಗೆ ಕಾನೂನಿನ ಅಡಿ ಇ-ಸ್ಟ್ಯಾಂಪ್. ಪ್ರಿಂಟರ್ ಅಥವಾ ನೋಟರಿ ಇಲ್ಲದೆ ನ್ಯಾಯಾಲಯದಲ್ಲಿ ಮಾನ್ಯ.",
    "why_b5_title": "ಮರೆಯಾಗದ ಪುರಾವೆ",
    "why_b5_desc": "ಮೂವ್-ಇನ್ ಫೋಟೋಗಳು ಮತ್ತು ಟಿಪ್ಪಣಿಗಳು ಮೊದಲ ದಿನ ಟೈಮ್‌ಸ್ಟ್ಯಾಂಪ್. ಮೂವ್-ಔಟ್ ಹೋಲಿಕೆ ಸ್ವಯಂಚಾಲಿತ.",
    "why_b6_title": "₹0 ಬ್ರೋಕರೇಜ್ ಚೌಕಾಸಿ",
    "why_b6_desc": "ಪಾರದರ್ಶಕ ಕಮಿಷನ್, ಮೊದಲೇ ತೋರಿಸಲ್ಪಟ್ಟಿದೆ. ಅಡ್ಡ ಶುಲ್ಕಗಳಿಲ್ಲ, ಬ್ರೋಕರ್ ನಾಟಕ ಇಲ್ಲ.",

    // Persona: Tenants
    "persona_tenant_label": "ಬಾಡಿಗೆದಾರರಿಗೆ",
    "persona_tenant_headline": "ನಿಮ್ಮ ಠೇವಣೆ\nನಿಮ್ಮದೇ.",
    "persona_tenant_body": "ಮಾಲಿಕರು ತಿಂಗಳ ತನಕ ನಿಮ್ಮ ಹಣ ಹಿಡಿದಿಡುತ್ತಾರೆ ಮತ್ತು ತಮ್ಮ ಷರತ್ತಿನ ಮೇಲೆ ಹಿಂದಿರುಗಿಸುತ್ತಾರೆ. Orello ಅದನ್ನು ಎಸ್ಕ್ರೋದಲ್ಲಿ ಇಡುತ್ತದೆ — ಲಾಕ್, ಟೈಮ್‌ಸ್ಟ್ಯಾಂಪ್, ನಿಮ್ಮ ಒಪ್ಪಿಗೆ ಮೇಲೆ ಬಿಡುಗಡೆ.",
    "persona_tenant_f1_t": "ಎಸ್ಕ್ರೋದಲ್ಲಿ ಠೇವಣೆ",
    "persona_tenant_f1_d": "ನಿಮ್ಮ ಹಣ Orello ಬಳಿ ಸುರಕ್ಷಿತವಾಗಿದೆ — ಮಾಲಿಕರ ಖಾತೆಯಲ್ಲಿ ಅಲ್ಲ. ನಿಮ್ಮ ಅನುಮತಿ ಇಲ್ಲದೆ ಅವರು ಮುಟ್ಟಲಾಗುವುದಿಲ್ಲ.",
    "persona_tenant_f2_t": "ಮೊದಲ ದಿನ ಪುರಾವೆ ಲಾಕ್",
    "persona_tenant_f2_d": "ಫೋಟೋ, ವೀಡಿಯೋ ಮತ್ತು ಸ್ಥಿತಿ ಟಿಪ್ಪಣಿಗಳು ಮೂವ್-ಇನ್‌ನಲ್ಲಿ ಟೈಮ್‌ಸ್ಟ್ಯಾಂಪ್‌ನೊಂದಿಗೆ ಸೀಲ್. ನಂತರ 'ಹಾಗೇ ಇತ್ತು' ಎಂಬ ವಾದ ಸಾಧ್ಯವಿಲ್ಲ.",
    "persona_tenant_f3_t": "ಪ್ರತಿ ಕಡಿತ ನೀವೇ ಅನುಮೋದಿಸಿ",
    "persona_tenant_f3_d": "ಒಂದು ಪೈಸ ಹೋಗುವ ಮೊದಲು ಪ್ರತಿ ಹಕ್ಕನ್ನು ಪರಿಶೀಲಿಸಿ. ಒಪ್ಪದ ಯಾವುದನ್ನಾದರೂ ಪ್ರಶ್ನಿಸಿ.",
    "persona_tenant_f4_t": "10 ದಿನದಲ್ಲಿ ವಿವಾದ ಪರಿಹಾರ",
    "persona_tenant_f4_d": "ಒಪ್ಪಂದ ಆಗದಿದ್ದರೆ Orello ಪುರಾವೆ ಪರಿಶೀಲಿಸಿ 10 ದಿನದಲ್ಲಿ ಬಾಧ್ಯಕರ ನಿರ್ಧಾರ ನೀಡುತ್ತದೆ. ವಕೀಲ ಇಲ್ಲ, ನ್ಯಾಯಾಲಯ ಇಲ್ಲ.",
    "persona_tenant_cta": "ಬಾಡಿಗೆದಾರರಾಗಿ ಸೈನ್ ಅಪ್ ಮಾಡಿ",

    // Persona: Landlords
    "persona_landlord_label": "ಮಾಲಿಕರಿಗೆ",
    "persona_landlord_headline": "ನಿಮ್ಮ ಆಸ್ತಿ.\nನಿಮ್ಮ ಷರತ್ತುಗಳು.\nಯಾವ ಆಶ್ಚರ್ಯವಿಲ್ಲ.",
    "persona_landlord_body": "ಹೆಚ್ಚಿನ ಮಾಲಿಕರು ಕೆಟ್ಟ ಬಾಡಿಗೆದಾರರಿಂದಲ್ಲ — ಕೆಟ್ಟ ದಾಖಲೆಗಳಿಂದ ನಷ್ಟ ಅನುಭವಿಸುತ್ತಾರೆ. Orello ನಿಮಗೆ ಪರಿಶೀಲಿತ ಬಾಡಿಗೆದಾರರು, ನ್ಯಾಯಾಲಯ-ಮಾನ್ಯ ಒಪ್ಪಂದಗಳು ಮತ್ತು ಕೀ ನೀಡುವ ಮೊದಲು ಕೋಣೆ-ಕೋಣೆ ಸಾಕ್ಷ್ಯ ನೀಡುತ್ತದೆ.",
    "persona_landlord_f1_t": "ಯಾರು ಬರುತ್ತಾರೆ ಎಂದು ತಿಳಿಯಿರಿ",
    "persona_landlord_f1_d": "ಪ್ರತಿ ಬಾಡಿಗೆದಾರರಿಗೆ ಕೀ ಸಿಗುವ ಮೊದಲು ಆಧಾರ್-KYC ಮಾಡಲಾಗುತ್ತದೆ. ಫೋಟೋ ಅಲ್ಲ, ಫಾರ್ವರ್ಡ್ ID ಅಲ್ಲ — ನಿಜವಾದ KYC.",
    "persona_landlord_f2_t": "ನ್ಯಾಯಾಲಯದಲ್ಲಿ ನಿಲ್ಲುವ ಒಪ್ಪಂದಗಳು",
    "persona_landlord_f2_d": "ಇ-ಸ್ಟ್ಯಾಂಪ್, ಡಿಜಿಟಲ್ ಸಹಿ ಮಾಡಿದ ಬಾಡಿಗೆ ಒಪ್ಪಂದ. ಭಾರತದಲ್ಲೆಲ್ಲ ಮಾನ್ಯ. ಯಾವಾಗ ಬೇಕಾದರೂ ಡೌನ್‌ಲೋಡ್. ನೋಟರಿ ಇಲ್ಲ.",
    "persona_landlord_f3_t": "ಬರುವ ಮೊದಲು ಫ್ಲ್ಯಾಟ್ ದಾಖಲು",
    "persona_landlord_f3_d": "ಫೋಟೋಗಳೊಂದಿಗೆ ಕೋಣೆ-ಕೋಣೆ ಸ್ಥಿತಿ ವರದಿ, ಮೂವ್-ಇನ್‌ನಲ್ಲಿ ಲಾಕ್. ಹೊರ ಹೋಗುವಾಗ ಹಾನಿಗೆ ಅಖಂಡ ಸಾಕ್ಷ್ಯ.",
    "persona_landlord_f4_t": "ಪ್ರತಿ ಬಾರಿ ಶುದ್ಧ ನಿರ್ಗಮನ",
    "persona_landlord_f4_d": "ಇಬ್ಬರೂ ಪ್ಲಾಟ್‌ಫಾರ್ಮ್‌ನಲ್ಲಿ ಕಡಿತ ಇತ್ಯರ್ಥ ಮಾಡಿಕೊಳ್ಳುತ್ತಾರೆ. ಕೊನೆಯ ದಿನ ಜಗಳ ಇಲ್ಲ, ವಾಟ್ಸ್‌ಆ್ಯಾಪ್‌ನಲ್ಲಿ ಬೆನ್ನಟ್ಟುವಿಕೆ ಇಲ್ಲ.",
    "persona_landlord_cta": "ಮಾಲಿಕರಾಗಿ ಸೈನ್ ಅಪ್ ಮಾಡಿ",

    // Persona: Brokers
    "persona_broker_label": "ಬ್ರೋಕರ್‌ಗಳು ಮತ್ತು ಏಜೆಂಟ್‌ಗಳಿಗೆ",
    "persona_broker_headline": "ಹೆಚ್ಚು ಡೀಲ್.\nಕಡಿಮೆ ಜಗಳ.",
    "persona_broker_body": "ನೀವು ಅರ್ಧ ಸಮಯ ಕಾಗದ ಕೆಲಸದಲ್ಲಿ ಮತ್ತು ಅರ್ಧ ಠೇವಣೆ ವಿವಾದ ಬಗೆಹರಿಸುವಲ್ಲಿ ಕಳೆಯುತ್ತೀರಿ. Orello ಎರಡನ್ನೂ ನಿಭಾಯಿಸುತ್ತದೆ — ನೀವು ಡೀಲ್ ಮುಚ್ಚಲು ಗಮನ ಹರಿಸಿ.",
    "persona_broker_f1_t": "ನಿಮಿಷಗಳಲ್ಲಿ ಒಪ್ಪಂದ",
    "persona_broker_f1_d": "ಒಂದೇ ಫ್ಲೋದಲ್ಲಿ ಕಾನೂನುಬದ್ಧ, ಇ-ಸ್ಟ್ಯಾಂಪ್ ಬಾಡಿಗೆ ಒಪ್ಪಂದ ರಚಿಸಿ. KYC, ಠೇವಣೆ, ಸಹಿಗಳು — ಕ್ಲೈಂಟ್ ಹೋಗುವ ಮೊದಲು ಮುಗಿದಿರುತ್ತದೆ.",
    "persona_broker_f2_t": "ಎಲ್ಲ, ಎಲ್ಲಿಯಾದರೂ ನೋಡಿ",
    "persona_broker_f2_d": "ನಿಮ್ಮ ಎಲ್ಲ ಒಪ್ಪಂದಗಳು, ವರದಿಗಳು ಮತ್ತು ಡೀಲ್ ಸ್ಥಿತಿ ಒಂದೇ ಡ್ಯಾಶ್‌ಬೋರ್ಡ್‌ನಲ್ಲಿ — ಯಾವ ಉಪಕರಣದಲ್ಲಾದರೂ, ಯಾವಾಗಲಾದರೂ.",
    "persona_broker_f3_t": "ಮಧ್ಯಸ್ಥಿಕೆ ನಿಲ್ಲಿಸಿ",
    "persona_broker_f3_d": "ವಿವಾದಗಳು Orello ಗೆ ಹೋಗುತ್ತವೆ, ನಿಮ್ಮ ಫೋನ್‌ಗೆ ಅಲ್ಲ. ಬಾಡಿಗೆದಾರರು ಮತ್ತು ಮಾಲಿಕರು ಪ್ಲಾಟ್‌ಫಾರ್ಮ್‌ನಲ್ಲಿ ಬಗೆಹರಿಸಿಕೊಳ್ಳುತ್ತಾರೆ.",
    "persona_broker_f4_t": "ಮುಚ್ಚುತ್ತ ಗಳಿಸಿ",
    "persona_broker_f4_d": "ಪ್ರತಿ ಠೇವಣೆ ರಕ್ಷಣೆಯ ಮೇಲೆ 15% ಕಮಿಷನ್. ಸ್ವಯಂಚಾಲಿತ ಟ್ರ್ಯಾಕ್, ಬೇಕಾದಾಗ ಹಿಂಪಡೆಯಿರಿ.",
    "persona_broker_cta": "ಬ್ರೋಕರ್ ಆಗಿ ಸೈನ್ ಅಪ್ ಮಾಡಿ",

    // Proof section
    "proof_label": "ಸಾಕ್ಷ್ಯ",
    "proof_heading_1": "ಸಾಕ್ಷ್ಯ",
    "proof_heading_2": "ತಾನೇ ಮಾತನಾಡುತ್ತದೆ.",
    "proof_stat1_label": "ಪ್ರಾರಂಭಿಸಲು",
    "proof_stat1_sub": "ಎಲ್ಲರಿಗೂ ಉಚಿತ",
    "proof_stat2_label": "ಡೀಲ್ ರಚಿಸಲು",
    "proof_stat2_sub": "KYC ನಿಂದ ಸಹಿ",
    "proof_stat3_label": "ವಿವಾದ ಪರಿಹಾರ",
    "proof_stat3_sub": "ಬಾಧ್ಯಕರ ನಿರ್ಧಾರ",
    "proof_stat4_label": "ಕಾನೂನು ಮಾನ್ಯ",
    "proof_stat4_sub": "ಇ-ಸ್ಟ್ಯಾಂಪ್ ಮತ್ತು ಸಹಿ",
    "proof_stat5_label": "ಪುರಾವೆ ಲಾಕ್",
    "proof_stat5_sub": "ಶಾಶ್ವತ ಟೈಮ್‌ಸ್ಟ್ಯಾಂಪ್",

    // FOMO / Social proof
    "fomo_label": "ಭಾರತದಾದ್ಯಂತ ಬಾಡಿಗೆದಾರರು ಮತ್ತು ಮಾಲಿಕರ ವಿಶ್ವಾಸ",
    "fomo_heading": "ನಿಜವಾದ ಜನರು. ನಿಜವಾದ ಠೇವಣೆ.\nನಿಜವಾದ ಪರಿಹಾರ.",
    "fomo_stat1_label": "ಬಾಡಿಗೆದಾರರಿಗೆ ಪೂರ್ಣ ಠೇವಣೆ ಹಿಂದಿರುಗಿ ಬಂತು",
    "fomo_stat2_label": "ಮಾಲಿಕರು ವಿವಾದ ಇಲ್ಲದೆ ಬಾಡಿಗೆ ಮುಗಿಸಿದರು",
    "fomo_stat3_label": "ವಿವಾದಗಳು 10 ದಿನಗಳಲ್ಲಿ ಬಗೆಹರಿದವು",
    "fomo_stat4_label": "ಬ್ರೋಕರ್‌ಗಳು ವೇಗವಾಗಿ ಡೀಲ್ ಮುಚ್ಚುತ್ತಿದ್ದಾರೆ",
    "review_1_quote": "ಗೋಡೆಯ ಗೀರಿನಿಂದ ನನ್ನ ಠೇವಣೆ ಹೋಗುತ್ತದೆ ಎಂದು ಖಚಿತವಾಗಿ ಯೋಚಿಸಿದ್ದೆ. Orello ಮೂವ್-ಇನ್ ಫೋಟೋ ₹45,000 ಉಳಿಸಿದವು.",
    "review_1_role": "ಬಾಡಿಗೆದಾರ",
    "review_1_location": "ಬಾಂದ್ರಾ, ಮುಂಬೈ",
    "review_2_quote": "ನನ್ನ ಕೊನೆಯ ಬಾಡಿಗೆದಾರ ತಿಳಿಸದೇ ಹೋದರು. Orello ನಿಂದ ಸಹಿ ಮಾಡಿದ ಒಪ್ಪಂದ, ದಾಖಲಿಸಿದ ಸ್ಥಿತಿ ಇತ್ತು ಮತ್ತು 8 ದಿನದಲ್ಲಿ ಇತ್ಯರ್ಥ ಆಯಿತು.",
    "review_2_role": "ಮಾಲಿಕ",
    "review_2_location": "ಕೋಥ್ರುಡ್, ಪುಣೆ",
    "review_3_quote": "ತಿಂಗಳಿಗೆ 6–8 ಡೀಲ್ ಮುಚ್ಚುತ್ತೇನೆ. Orello ಪ್ರತಿಯೊಂದನ್ನೂ ಶುದ್ಧ ಮಾಡುತ್ತದೆ — ವಾಟ್ಸ್‌ಆ್ಯಾಪ್ ವಾದ ಇಲ್ಲ, ಸಹಿ ಹಿಂಬಾಲಿಸುವಿಕೆ ಇಲ್ಲ.",
    "review_3_role": "ಬ್ರೋಕರ್",
    "review_3_location": "ಅಂಧೇರಿ, ಮುಂಬೈ",
    "review_4_quote": "ಮಾಲಿಕ ಪೇಂಟಿಂಗ್‌ಗಾಗಿ ₹30,000 ಕಡಿಯಲು ಪ್ರಯತ್ನಿಸಿದರು. Orello ಮೂವ್-ಇನ್‌ನಲ್ಲಿ ಗೋಡೆಗಳು ಸರಿಯಾಗಿದ್ದವು ಎಂದು ತೋರಿಸಿತು. ಪ್ರತಿ ರೂಪಾಯಿ ಹಿಂತಿರುಗಿ ಬಂತು.",
    "review_4_role": "ಬಾಡಿಗೆದಾರ",
    "review_4_location": "ಕೋರಮಂಗಲ, ಬೆಂಗಳೂರು",
    "review_5_quote": "ಅಂತಿಮವಾಗಿ ಎರಡೂ ಕಡೆಯನ್ನು ರಕ್ಷಿಸುವ ಪ್ಲ್ಯಾಟ್‌ಫಾರ್ಮ್. ನನ್ನ ಬಾಡಿಗೆದಾರರಿಗೆ ಠೇವಣೆ ನೀಡಲು ಮತ್ತು ನನಗೆ ಸ್ವೀಕರಿಸಲು ಸುರಕ್ಷಿತ ಅನಿಸಿತು.",
    "review_5_role": "ಮಾಲಿಕ",
    "review_5_location": "ಅಡ್ಯಾರ್, ಚೆನ್ನೈ",
    "review_6_quote": "ಕಳೆದ ತಿಂಗಳು 3 ಬ್ರೋಕರ್‌ಗಳನ್ನು ರೆಫರ್ ಮಾಡಿದೆ, ₹750 ಬೋನಸ್ ಗಳಿಸಿದೆ. ಮತ್ತು ಪ್ರಕ್ರಿಯೆಯ ಮೇಲೆ ನಂಬಿಕೆ ಇರುವುದರಿಂದ ಗ್ರಾಹಕರು ಹಿಂದಿರುಗಿ ಬರುತ್ತಾರೆ.",
    "review_6_role": "ಏಜೆಂಟ್",
    "review_6_location": "ಜುಬಿಲಿ ಹಿಲ್ಸ್, ಹೈದರಾಬಾದ್",

    // Broker CTA
    "broker_cta_label": "ಬ್ರೋಕರ್‌ಗಳು ಮತ್ತು ಏಜೆಂಟ್‌ಗಳಿಗೆ",
    "broker_cta_heading_1": "ನಿಮ್ಮ ಆಸ್ತಿ ನಿರ್ವಹಣಾ ಪ್ಲ್ಯಾಟ್‌ಫಾರ್ಮ್.",
    "broker_cta_heading_2": "ಉಚಿತವಾಗಿ ಪ್ರಾರಂಭ.",
    "broker_cta_body": "ಒಪ್ಪಂದಗಳು, KYC, ಠೇವಣೆ, ಸ್ಥಿತಿ ವರದಿಗಳು, ವಿವಾದ ಪರಿಹಾರ — ಒಂದೇ ಡ್ಯಾಶ್‌ಬೋರ್ಡ್‌ನಿಂದ ಬಾಡಿಗೆ ಡೀಲ್‌ನ ಪ್ರತಿ ಭಾಗ.",
    "broker_cta_btn": "ನಿಮ್ಮ ಉಚಿತ ಖಾತೆ ರಚಿಸಿ",
    "broker_cta_fine": "ಕ್ರೆಡಿಟ್ ಕಾರ್ಡ್ ಇಲ್ಲ. ಸೆಟ್‌ಅಪ್ ಶುಲ್ಕ ಇಲ್ಲ. ಅಪರಿಮಿತ ಒಪ್ಪಂದಗಳು.",

    // Final CTA
    "final_cta_heading_1": "ನಿಮ್ಮ ಮುಂದಿನ ಬಾಡಿಗೆ.",
    "final_cta_heading_2": "ರಕ್ಷಿತ.",
    "final_cta_body": "ಕ್ರೆಡಿಟ್ ಕಾರ್ಡ್ ಇಲ್ಲ. ಸೆಟ್‌ಅಪ್ ಶುಲ್ಕ ಇಲ್ಲ. ನಿಮಿಷಗಳಲ್ಲಿ ಸಿದ್ಧ.",
    "final_cta_btn": "ಉಚಿತ ಖಾತೆ ರಚಿಸಿ",

    // Login page
    "login_welcome": "ಮತ್ತೆ ಸ್ವಾಗತ",
    "login_subtitle_default": "ನಿಮ್ಮ ಫೋನ್ ಅಥವಾ ಇಮೇಲ್ ಮತ್ತು ಪಾಸ್‌ವರ್ಡ್‌ನಿಂದ ಸೈನ್ ಇನ್ ಮಾಡಿ.",
    "login_subtitle_tenant": "ನಿಮ್ಮ ಠೇವಣಿ ರಕ್ಷಿಸಲು ಸೈನ್ ಇನ್ ಮಾಡಿ",
    "login_subtitle_landlord": "ನಿಮ್ಮ ಬಾಡಿಗೆದಾರನನ್ನು ಪರಿಶೀಲಿಸಲು ಸೈನ್ ಇನ್ ಮಾಡಿ",
    "login_subtitle_broker": "ಪ್ರತಿ ಡೀಲ್‌ನಲ್ಲಿ ಕಮಿಷನ್ ಗಳಿಸಲು ಸೈನ್ ಇನ್ ಮಾಡಿ",
    "login_phone_label": "ಫೋನ್ ಅಥವಾ ಇಮೇಲ್",
    "login_phone_placeholder": "9876543210 ಅಥವಾ you@example.com",
    "login_password_label": "ಪಾಸ್‌ವರ್ಡ್",
    "login_forgot": "ಪಾಸ್‌ವರ್ಡ್ ಮರೆತಿದ್ದೀರಾ?",
    "login_btn": "ಸೈನ್ ಇನ್ ಮಾಡಿ",
    "login_btn_loading": "ಸೈನ್ ಇನ್ ಆಗುತ್ತಿದೆ…",
    "login_no_account": "ಖಾತೆ ಇಲ್ಲವೇ?",
    "login_signup": "ಸೈನ್ ಅಪ್ ಮಾಡಿ",

    // Agreement Form Builder (Kannada)
    "form_step_0_title": "ಹಂತ 0: ನಿಮ್ಮ ಒಪ್ಪಂದ ಪ್ರಕಾರ ಆರಿಸಿ",
    "form_step_0_description": "ನಿಮ್ಮ ಅಗತ್ಯಗಳಿಗೆ ಸರಿಹೊಂದುವ ಒಪ್ಪಂದ ಸ್ವರೂಪ ಆರಿಸಿ।",
    "form_step_1_title": "ಹಂತ 1: ಮಾಲಿಕರ ವಿವರಗಳು",
    "form_step_2_title": "ಹಂತ 2: ಬಾಡಿಗೆದಾರರ ವಿವರಗಳು",
    "form_step_4_title": "ಹಂತ 4: ಬಾಡಿಗೆಯ ನಿಯಮಗಳು",
    "form_step_5_title": "ಹಂತ 5: ಒಪ್ಪಂದ ವೇಳಾವಧಿ ಮತ್ತು ಅಂತಿಮ ದಿನಾಂಕ",
    "form_step_8_title": "ಹಂತ 8: ಒಪ್ಪಂದ ಸಾಂಸ್ಥಿಕ ಮಾಡಿ",
    "form_estamped_title": "ಇ-ಸ್ಟ್ಯಾಂಪ್ ಒಪ್ಪಂದ",
    "form_eagreement_title": "ಇ-ಒಪ್ಪಂದ",
    "form_full_name": "ಪೂರ್ಣ ಹೆಸರು",
    "form_start_date": "ಪ್ರಾರಂಭ ದಿನಾಂಕ",
    "form_end_date": "ಅಂತಿಮ ದಿನಾಂಕ",
    "form_back": "← ಹಿಂದಿರುಗಿ",
    "form_next": "ಮುಂದೆ →",
    "form_submit": "ಒಪ್ಪಂದ ರಚಿಸಿ",

    // Main Dashboard UI (Kannada)
    "page_create_agreement": "ಒಪ್ಪಂದ ರಚಿಸಿ",
    "page_fill_details": "ಒಪ್ಪಂದ ವಿವರಣೆ ಭರ್ತಿ ಮಾಡಿ। ಲೈವ್ ಪೂರ್ವವೀಕ್ಷಣೆ ನೈಜ ಸಮಯದಲ್ಲಿ ಅಪ್ಡೇಟ್ ಆಗುತ್ತದೆ.",
    "page_select_state": "ಒಪ್ಪಂದ ಟೆಂಪ್ಲೇಟ್‌ಗಾಗಿ ರಾಜ್ಯವನ್ನು ಆಯ್ಕೆ ಮಾಡಿ",
    "page_select_state_desc": "ಸೂಕ್ತ ಒಪ್ಪಂದ ಟೆಂಪ್ಲೇಟ್ ಲೋಡ್ ಮಾಡಲು ನಿಮ್ಮ ರಾಜ್ಯವನ್ನು ಆಯ್ಕೆ ಮಾಡಿ। ವಿವಿಧ ರಾಜ್ಯಗಳು ಪಟ್ಟಾ ಒಪ್ಪಂದಗಳಿಗೆ ವಿವಿಧ ಕಾನೂನು ಅವಶ್ಯಕತೆಗಳನ್ನು ಹೊಂದಿವೆ।",
    "page_select_state_label": "📍 ನಿಮ್ಮ ರಾಜ್ಯವನ್ನು ಆಯ್ಕೆ ಮಾಡಿ",
    "page_agreement_format": "📋 ಒಪ್ಪಂದ ಫಾರ್ಮ್ಯಾಟ್",
    "page_send_invites": "📧 ಒಪ್ಪಂದ ಹಂಚಿಕೊಳ್ಳಿ",
    "page_pdf_preview": "📄 ಒಪ್ಪಂದ ಪೂರ್ವವೀಕ್ಷಣೆ ಮತ್ತು ಡೌನ್‌ಲೋಡ್",
    "page_registration_status": "ನೋಂದಣಿ ಸ್ಥಿತಿ",
    "page_landlord": "🏠 ಮಾಲಿಕ",
    "page_tenant": "👤 ಬಾಡಿಗೆದಾರ",
    "page_ready_to_sign": "ಸಹಿ ಮಾಡಲು ಸಿದ್ಧ",
    "page_registered": "✓ ನೋಂದಾಯಿತ",
    "page_not_registered": "○ ನೋಂದಾಯಿತವಲ್ಲ",

    // Sidebar Navigation (Kannada)
    "sidebar_home": "ಹೋಮ್",
    "sidebar_overview": "ಕ್ರೀಡಾ ಸಾರಾಂಶ",
    "sidebar_create_agreement": "ಒಪ್ಪಂದ ರಚಿಸಿ",
    "sidebar_my_team": "ನನ್ನ ತಂಡ",
    "sidebar_my_deals": "ನನ್ನ ಡೀಲ್‌ಗಳು",
    "sidebar_all_deals": "ಎಲ್ಲಾ ಡೀಲ್‌ಗಳು",
    "sidebar_my_earnings": "ನನ್ನ ಆದಾಯ",
    "sidebar_my_rental": "ನನ್ನ ಬಾಡಿಗೆ",
    "sidebar_deposits": "ಠೇವಣೆಗಳು",
    "sidebar_wallet": "ವಾಲೆಟ್",
    "sidebar_notifications": "ಅಧಿಸೂಚನೆಗಳು",
    "sidebar_analytics": "ವಿಶ್ಲೇಷಣೆ",
    "sidebar_business_profile": "ವ್ಯವಹಾರ ಪ್ರೊಫೈಲ್",
    "sidebar_referral": "ನಿರ್ದೇಶನ",
    "sidebar_profile": "ಪ್ರೊಫೈಲ್",
    "sidebar_settings": "ಸೆಟ್ಟಿಂಗ್‌ಗಳು",
    "sidebar_support": "ಬೆಂಬಲ",
    "sidebar_help": "ಸಹಾಯ",
  },

  gu: {
    // Navigation
    "nav_for_tenants": "ભાડેકરો માટે",
    "nav_for_landlords": "મકાનમાલિકો માટે",
    "nav_for_brokers": "બ્રોકરો અને એજન્ટો માટે",
    "nav_trust": "વિશ્વાસ",
    "nav_about": "આমારા વિશે",
    "nav_contact": "સંપર્ક કરો",
    "nav_login": "લૉગિન",
    "nav_get_started": "મફતમાં શરૂ કરો",

    // Hero & Main
    "no_deposit_fights": "ડિપોઝિટ પર કોઈ ઝઘડો નહીં.",
    "renting_without_fights": "ભાડું, ઝઘડો વિના.",
    "hero_sub": "Orello કરાર, ડિપોઝિટ અને મિલકત રિપોર્ટ સંભાળે છે — બધા માટે ન્યાય.",
    "get_started": "મફતમાં શરૂ કરો",
    "see_how": "તે કેવી રીતે કામ કરે તે જુઓ",
    "login": "લૉગિન",
    "sign_up": "સાઇન અપ",
    "create_free_account": "મફત ખાતું બનાવો",

    // For Tenants
    "your_deposit_deserves": "તમારો ડિપોઝિટ પાછો આવવાને લાયક છે.",
    "deposit_protected": "₹45,000+ ડિપોઝિટ સુરક્ષિત",
    "avg_resolution": "7 દિવસ સરેરાશ નિરાકરણ",
    "success_rate": "99.2% વિવાદ હલ",
    "deposit_in_escrow": "એસ્ક્રોમાં ડિપોઝિટ",
    "proof_locked": "પહેલા દિવસે પુરાવો લૉક",
    "you_approve": "તમે દરેક કપાતને મંજૂરી આપો",
    "disputes_resolved": "10 દિવસમાં વિવાદ હલ",

    // For Landlords
    "rent_with_confidence": "આત્મવિશ્વાસ સાથે ભાડું આપો.",
    "properties_listed": "12,000+ ચકાસાયેલી મિલકતો",
    "avg_deduction": "₹8,500 સરેરાશ કપાત",
    "paperwork_free": "100% ડિજિટલ પ્રક્રિયા",
    "verified_tenants": "ચકાસાયેલ ભાડેકરો",
    "court_valid": "આદાલત-માન્ય કરાર",
    "property_documented": "મિલકત દલીલો",
    "clean_exits": "સ્વચ્છ બહાર નીકળવું",

    // For Brokers
    "close_more_deals": "વધુ ડીલ બંધ કરો. ઓછો ઝઘડો.",
    "brokers_partnered": "8,500+ સક્રિય એજન્ટો",
    "avg_earnings": "₹4,500 પ્રતિ ડીલ",
    "faster_closures": "5 મિનિટ સેટઅપથી સહી",
    "earn_on_deals": "દરેક ડીલ પર કમાણી કરો",

    // Trust & Security
    "we_protect_everyone": "આપણે બધાને રક્ષણ આપીએ છીએ.",
    "regulated_escrow": "નિયંત્રિત એસ્ક્રો",
    "legal_agreements": "કાનૂની કરાર",
    "timestamped_records": "સમયમુદ્રિત રેકોર્ડ્સ",

    // About
    "renting_broken": "ભાડું આટલું તૂટેલું ન હોવું જોઈએ.",
    "we_started": "અમે શરૂ કર્યું કારણ કે અમે ગુસ્સાયેલા હતા.",
    "fair": "ન્યાયી",
    "fast": "ઝડપી",
    "transparent": "પારદર્શક",

    // Contact
    "get_in_touch": "આપણા સાથે સંપર્ક કરો",
    "send_message": "અમને સંદેશ મોકલો",
    "email": "ઈ-મેઈલ",
    "whatsapp": "વોટ્સએપ",
    "office": "કાર્યાલય",
    "full_name": "પૂર્ણ નામ",
    "email_address": "ઈ-મેઈલ સરનામું",
    "message": "સંદેશ",

    // Footer
    "terms": "શરતો",
    "privacy": "ગોપનીયતા",
    "copyright": "© 2025 Orello. બધા અધિકાર આરક્ષિત.",

    // Common
    "sound_familiar": "આ પરિચિત લાગે છે?",
    "simple_secure_done": "સરળ. સુરક્ષિત. પૂર્ણ.",
    "built_for_everyone": "બધા માટે બનેલું",
    "why_orello": "શા માટે Orello?",
    "simple_pricing": "સરળ કિંમત",
    "start_today": "આજ શરૂ કરો. મફતમાં.",
    "tenants": "ભાડેકરો",
    "landlords": "મકાનમાલિકો",
    "brokers": "બ્રોકરો",
    "made_for_india": "ભારત માટે બનેલું",
    "how_it_works": "તે કેવી રીતે કામ કરે",
    "language": "ભાષા",

    // Hero section
    "hero_built_for": "ભારતીય ભાડા માટે બનેલું",
    "hero_cta_get_started": "મફતમાં શરૂ કરો",
    "hero_cta_see_how": "કેવી રીતે કામ કરે તે જુઓ",
    "hero_ticker_setup": "₹0 સેટઅપ ફી",
    "hero_ticker_deal": "5 મિનિટમાં ડીલ બનાવો",
    "hero_ticker_disputes": "10 દિવસમાં નિકાલ",
    "hero_ticker_legal": "100% કાયદેસર કરાર",

    // Ticker bar
    "ticker_escrow": "એસ્ક્રોમાં ડિપોઝિટ",
    "ticker_legal": "કાયદેસર કરાર",
    "ticker_aadhaar": "આધાર-ચકાસાયેલ ભાડેકરો",
    "ticker_condition": "ટાઇમ-સ્ટેમ્પ સ્થિતિ અહેવાલ",
    "ticker_dispute": "ન્યાયી વિવાદ નિવારણ",
    "ticker_nowhatsapp": "વૉટ્સએપ ઝઘડા નહીં",
    "ticker_3way": "3-માર્ગ ચકાસાયેલ ડીલ",
    "ticker_india": "ભારત માટે બનેલું",

    // Problem section
    "problem_label": "સમસ્યા",
    "problem_heading": "આ પરિચિત લાગે છે?",
    "problem_01_title": "ડિપોઝિટ બસ... ગાયબ",
    "problem_01_body": "₹80,000 આપ્યા. ₹20,000 પાછા મળ્યા. કોઈ યાદી નહીં, ફોટો નહીં, સ્પષ્ટતા નહીં. માત્ર એક આંકડો.",
    "problem_01_tag": "દર 3 ભાડેકરોમાંથી 1 ને થાય છે",
    "problem_02_title": "પુરાવો નહીં, કેસ નહીં",
    "problem_02_body": "ભાડેકર કહે છે ખાંચો પહેલેથી હતી. માલિક કહે છે નહોતી. બેમાંથી કોઈ પાસે ફોટો નથી. કોઈ જીતતું નથી.",
    "problem_02_tag": "દસ્તાવેજ નહીં = ઉકેલ નહીં",
    "problem_03_title": "વૉટ્સએપ કાનૂની દસ્તાવેજ નથી",
    "problem_03_body": "દરેક મૂવ-આઉટ દલીલ-સ્ક્રીનશૉટ-મૌનની સાંકળ બની જાય છે. પછી કોઈ જવાબ આપવાનું બંધ કરે છે.",
    "problem_03_tag": "છેવટે બ્રોકરને દોષ મળે છે",

    // How it works
    "how_label": "Orello નો માર્ગ",
    "how_heading_1": "એક ભાડું.",
    "how_heading_2": "રક્ષણના છ પડાવ.",
    "how_body": "દરેક ભારતીય ભાડું એ જ જોખમી રાહ અનુસરે છે. Orello દરેક પગલે તમારી રક્ષા કરે છે — સાચી જગ્યા શોધવાથી ડિપોઝિટ પાછી મેળવવા સુધી.",
    "how_step_01_title": "તમારો એજન્ટ શોધો",
    "how_step_01_desc": "Orello પર ચકાસાયેલ બ્રોકર્સ અને એજન્ટ જુઓ. સાચી રેટિંગ, સાચી ડીલ, પારદર્શી કમિશન.",
    "how_step_02_title": "કરાર પર સહી કરો",
    "how_step_02_desc": "ઈ-સ્ટેમ્પ, ન્યાયાલય-માન્ય ભાડા કરાર. બધા પક્ષ ડિજિટલ સહી કરે. નોટરી નહીં, પ્રિન્ટર નહીં.",
    "how_step_03_title": "ડિપોઝિટ એસ્ક્રોમાં સુરક્ષિત",
    "how_step_03_desc": "ભાડેકર ચૂકવે — પૈસા Orello પાસે રહે, માલિક પાસે નહીં. બંને સહમત ન થાય ત્યાં સુધી લૉક.",
    "how_step_04_title": "મૂવ-ઇન રિપોર્ટ લૉક",
    "how_step_04_desc": "દીવાલ, ફ્લોર, ફિક્સ્ચર — ફોટો, ટાઇમ-સ્ટેમ્પ, બંનેની સહમતિ. પછી 'પહેલેથી આવું હતું' ની દલીલ નહીં.",
    "how_step_05_title": "નિશ્ચિંત ભાડા-વાસ",
    "how_step_05_desc": "Orello દ્વારા ભાડું ભરો. બધું એક જ જગ્યાએ ટ્રૅક. રિન્યૂઅલ રિમાઇન્ડર, સમારકામ વિનંતી — બધું એક ફીડમાં.",
    "how_step_06_title": "નાટક વગર નિકાલ",
    "how_step_06_desc": "મૂવ-આઉટ રિપોર્ટ, મૂવ-ઇન સાથે સરખાવો. સાચા કાપ મિનિટોમાં નક્કી. વિવાદ? Orello ચુકાદો આપે — તટસ્થ, ઝડપી, અંતિમ.",
    "how_bottom_pill": "દરેક પગલું ડિજિટલ, ટાઇમ-સ્ટેમ્પ અને કાનૂની રૂપે બંધનકારક છે.",

    // Why Orello
    "why_label": "Orello શા માટે",
    "why_heading_1": "ભારતીયો ખરેખર",
    "why_heading_2": "ભાડું કરે છે તે માટે બનેલું.",
    "why_b1_title": "પૈસા સુરક્ષિત લૉક",
    "why_b1_desc": "તમારી ડિપોઝિટ Orello ના નિયંત્રિત એસ્ક્રોમાં રહે. બંને સહમત થાય ત્યારે જ છૂટે.",
    "why_b2_title": "15 મિનિટમાં સેટઅપ",
    "why_b2_desc": "કરાર, KYC, ડિપોઝિટ, સ્થિતિ અહેવાલ — ફોન પરથી એક જ બેઠકમાં.",
    "why_b3_title": "ન્યાયી વિવાદ નિવારણ",
    "why_b3_desc": "બંને પક્ષ પુરાવા રજૂ કરે. Orello સાક્ષ્ય પર નિર્ણય લે — અનૌપચારિક ચા-ઠાર નહીં.",
    "why_b4_title": "ન્યાયાલય-માન્ય કરાર",
    "why_b4_desc": "ભારતીય ભાડા કાયદા હેઠળ ઈ-સ્ટેમ્પ. પ્રિન્ટર કે નોટરી વિના ન્યાયાલયમાં ગ્રાહ્ય.",
    "why_b5_title": "ઝાંખો ન પડે એ પુરાવો",
    "why_b5_desc": "મૂવ-ઇન ફોટો અને નોંધ પહેલા દિવસે ટાઇમ-સ્ટેમ્પ. મૂવ-આઉટ સરખામણી આપમેળે.",
    "why_b6_title": "₹0 બ્રોકરેજ મોલભાવ",
    "why_b6_desc": "પારદર્શી કમિશન, આગળથી જ જણાવ્યું. છૂપી ફી નહીં, બ્રોકર નાટક નહીં.",

    // Persona: Tenants
    "persona_tenant_label": "ભાડેકરો માટે",
    "persona_tenant_headline": "તમારી ડિપોઝિટ\nતમારી છે.",
    "persona_tenant_body": "માલિકો મહિનાઓ સુધી પૈસા રોકી રાખે અને પોતાની શરતે પાછા આપે. Orello એ એસ્ક્રોમાં રાખે — લૉક, ટાઇમ-સ્ટેમ્પ, અને ફક્ત તમારી સંમતિ પર છૂટે.",
    "persona_tenant_f1_t": "એસ્ક્રોમાં ડિપોઝિટ",
    "persona_tenant_f1_d": "તમારા પૈસા Orello પાસે સુરક્ષિત — માલિકના ખાતામાં નહીં. તમારી મંજૂરી વિના સ્પર્શ ન થઈ શકે.",
    "persona_tenant_f2_t": "પહેલા દિવસે પુરાવો લૉક",
    "persona_tenant_f2_d": "ફોટો, વિડિયો અને સ્થિતિ નોંધ મૂવ-ઇન વખતે ટાઇમ-સ્ટેમ્પ સાથે સીલ. પછી 'આમ જ હતું' ન ચાલે.",
    "persona_tenant_f3_t": "દરેક કાપ તમે મંજૂર કરો",
    "persona_tenant_f3_d": "એક પૈસો હલે તે પહેલા દરેક દાવો જુઓ. ન ગમે તેને પડકારો.",
    "persona_tenant_f4_t": "10 દિવસમાં વિવાદ ઉકેલ",
    "persona_tenant_f4_d": "સહમતિ ન થાય તો Orello પુરાવા જોઈ 10 દિવસમાં બંધનકારક ચુકાદો આપે. વકીલ નહીં, કોર્ટ નહીં.",
    "persona_tenant_cta": "ભાડેકર તરીકે સાઇન અપ કરો",

    // Persona: Landlords
    "persona_landlord_label": "મકાનમાલિકો માટે",
    "persona_landlord_headline": "તમારી મિલકત.\nતમારી શરતો.\nકોઈ આશ્ચર્ય નહીં.",
    "persona_landlord_body": "મોટા ભાગના માલિકો ખરાબ ભાડેકરોથી નહીં — ખરાબ દસ્તાવેજીકરણથી નુકસાન ઉઠાવે છે. Orello ચકાસાયેલ ભાડેકર, ન્યાયાલય-માન્ય કરાર અને ચાવી આપ્યા પહેલા ઓરડા-ઓરડા પુરાવો આપે.",
    "persona_landlord_f1_t": "કોણ આવે છે તે જાણો",
    "persona_landlord_f1_d": "દરેક ભાડેકરને ચાવી મળે તે પહેલા આધાર-KYC થાય. ફોટો નહીં, ફૉર્વર્ડ ID નહીં — સાચી KYC.",
    "persona_landlord_f2_t": "ન્યાયાલયમાં ટકે એ કરાર",
    "persona_landlord_f2_d": "ઈ-સ્ટેમ્પ, ડિજિટલ સહી ભાડા કરાર. ભારતમાં ગમે ત્યાં માન્ય. ગમ્યારે ડાઉનલોડ. નોટરી નહીં.",
    "persona_landlord_f3_t": "આવ્યા પહેલા ફ્લૅટ નોંધ",
    "persona_landlord_f3_d": "ફોટો સાથે ઓરડા-ઓરડા સ્થિતિ અહેવાલ, મૂવ-ઇન વખતે લૉક. મૂવ-આઉટ વખતે નુકસાનનો અખંડ પુરાવો.",
    "persona_landlord_f4_t": "દરેક વખતે ચોખ્ખી વિદાય",
    "persona_landlord_f4_d": "બંને પ્લૅટફૉર્મ પર કાપ ઇ નક્કી કરે. છેલ્લા દિવસ ઝઘડો નહીં, વૉટ્સએપ પર પીછો નહીં.",
    "persona_landlord_cta": "મકાનમાલિક તરીકે સાઇન અપ કરો",

    // Persona: Brokers
    "persona_broker_label": "બ્રોકર્સ અને એજન્ટો માટે",
    "persona_broker_headline": "વધુ ડીલ.\nઓછા ઝઘડા.",
    "persona_broker_body": "તમે અડધો સમય કાગળ-કામ અને અડધો ડિપોઝિટ ઝઘડા ઉકેલવામાં ખર્ચો. Orello બંને સંભાળે — તમે ડીલ બંધ કરવા પર ધ્યાન આપો.",
    "persona_broker_f1_t": "મિનિટોમાં કરાર",
    "persona_broker_f1_d": "એક ફ્લોમાં કાયદેસર, ઈ-સ્ટેમ્પ ભાડા કરાર બનાવો. KYC, ડિપોઝિટ, સહી — ક્લાઇન્ટ જાય તે પહેલા.",
    "persona_broker_f2_t": "બધું, ગમ્ ત્યાં જુઓ",
    "persona_broker_f2_d": "બધા કરાર, અહેવાલ અને ડીલ સ્ટેટસ એક ડૅશબૉર્ડ — ગમે ત્યારે, ગમ્ ઉપકરણ.",
    "persona_broker_f3_t": "વચ્ચેની ભૂમિકા છોડો",
    "persona_broker_f3_d": "વિવાદ Orello પાસે જાય, તમારા ફોને નહીં. ભાડેકર અને માલિક પ્લૅટફૉર્મ પર ઉકેલે.",
    "persona_broker_f4_t": "બંધ કરો, કમાઓ",
    "persona_broker_f4_d": "દરેક ડિપોઝિટ સુરક્ષા પર 15% કમિશન. આપોઆપ ટ્રૅક, ઇચ્છ્યારે ઉપાડો.",
    "persona_broker_cta": "બ્રોકર તરીકે સાઇન અપ કરો",

    // Proof section
    "proof_label": "પુરાવો",
    "proof_heading_1": "પુરાવો",
    "proof_heading_2": "ખુદ બોલે છે.",
    "proof_stat1_label": "શરૂ કરવા માટે",
    "proof_stat1_sub": "બધા માટે મફત",
    "proof_stat2_label": "ડીલ બનાવવા",
    "proof_stat2_sub": "KYC થી સહી",
    "proof_stat3_label": "વિવાદ નિવારણ",
    "proof_stat3_sub": "બંધનકારક ચુકાદો",
    "proof_stat4_label": "કાયદેસર માન્ય",
    "proof_stat4_sub": "ઈ-સ્ટેમ્પ અને સહી",
    "proof_stat5_label": "પુરાવો લૉક",
    "proof_stat5_sub": "કાયમ ટાઇમ-સ્ટેમ્પ",

    // FOMO / Social proof
    "fomo_label": "ભારત ભરના ભાડેકરો અને મકાનમાલિકોનો વિશ્વાસ",
    "fomo_heading": "સાચા લોકો. સાચી ડિપોઝિટ.\nસાચો ઉકેલ.",
    "fomo_stat1_label": "ભાડેકરોને પૂરી ડિપોઝિટ પાછી મળી",
    "fomo_stat2_label": "માલિકોએ ઝઘડા વગર ભાડૂ પૂરૂ કર્યું",
    "fomo_stat3_label": "વિવાદ 10 દિવસ કરતા ઓછા સમયમાં ઉકેલ્યા",
    "fomo_stat4_label": "બ્રોકર ઝડપથી ડીલ બંધ કરી રહ્યા છે",
    "review_1_quote": "દીવાલ પરની ખાંચો ને કારણે ડિપોઝિટ જશે એ નક્કી જ હતું. Orello ના મૂવ-ઇન ફોટોએ ₹45,000 બચાવ્યા.",
    "review_1_role": "ભાડેકર",
    "review_1_location": "બાંદ્રા, મુંબઈ",
    "review_2_quote": "છેલ્લો ભાડેકર જણાવ્યા વિના ગયો. Orello ને લીધે સહી-કરાર, નોંધ સ્થિતિ હતી અને 8 દિવસમાં હિસાબ થઈ ગયો.",
    "review_2_role": "મકાનમાલિક",
    "review_2_location": "કોથ્રૂડ, પુણે",
    "review_3_quote": "હું મહિને 6–8 ડીલ બંધ કરું છું. Orello દરેકને ચોખ્ખી બનાવે — વૉટ્સએપ ઝઘડો નહીં, સહી માટે પીછો નહીં.",
    "review_3_role": "બ્રોકર",
    "review_3_location": "અંધેરી, મુંબઈ",
    "review_4_quote": "માલિકે રંગ-કામ માટે ₹30,000 કાપવા ચાહ્યા. Orello એ બતાવ્યું મૂવ-ઇને દીવાલ ઠીક હતી. એ-એ પૈસો પાછો મળ્યો.",
    "review_4_role": "ભાડેકર",
    "review_4_location": "કોરમંગલા, બૅંગ્લોર",
    "review_5_quote": "આખરે એ પ્લૅટફૉર્મ જે બંને બાજુ રક્ષણ આપે. ભાડેકરને ડિપોઝિટ ભરવા અને મને લેવા સુરક્ષિત લાગ્યું.",
    "review_5_role": "મકાનમાલિક",
    "review_5_location": "અડ્યાર, ચૅન્નઈ",
    "review_6_quote": "ગયા મહિને 3 બ્રોકર રેફર કર્યા, ₹750 બોનસ મળ્યો. ગ્રાહકો પ્રક્રિયા પર ભરોસો હોવાથી પાછા આવે છે.",
    "review_6_role": "એજન્ટ",
    "review_6_location": "જ્યુબિલી હિલ્સ, હૈદ્રાબાદ",

    // Broker CTA
    "broker_cta_label": "બ્રોકર્સ અને એજન્ટો માટે",
    "broker_cta_heading_1": "તમારો પ્રૉપર્ટી મૅનેજમૅન્ટ પ્લૅટફૉર્મ.",
    "broker_cta_heading_2": "શરૂ કરવા મફત.",
    "broker_cta_body": "કરાર, KYC, ડિપોઝિટ, સ્થિતિ અહેવાલ, વિવાદ નિવારણ — એક ડૅશબૉર્ડ પરથી ભાડા ડીલ ના દરેક ભાગ.",
    "broker_cta_btn": "તમારું મફત ખાતું બનાવો",
    "broker_cta_fine": "ક્રૅડિટ કાર્ડ નહીં. સેટઅપ ફી નહીં. અમર્યાદ કરાર.",

    // Final CTA
    "final_cta_heading_1": "તમારું આગળનું ભાડું.",
    "final_cta_heading_2": "સુરક્ષિત.",
    "final_cta_body": "ક્રૅડિટ કાર્ડ નહીં. સેટઅપ ફી નહીં. મિનિટોમાં તૈયાર.",
    "final_cta_btn": "મફત ખાતું બનાવો",

    // Login page
    "login_welcome": "પાછા સ્વાગત છે",
    "login_subtitle_default": "તમારા ફોન અથવા ઈમેઈલ અને પાસવર્ડ વડે સાઈન ઈન કરો.",
    "login_subtitle_tenant": "તમારી ડિપોઝિટ સુરક્ષિત કરવા સાઈન ઈન કરો",
    "login_subtitle_landlord": "તમારા ભાડૂઆતની ચકાસણી કરવા સાઈન ઈન કરો",
    "login_subtitle_broker": "દરેક ડીલ પર કમિશન મેળવવા સાઈન ઈન કરો",
    "login_phone_label": "ફોન અથવા ઈમેઈલ",
    "login_phone_placeholder": "9876543210 અથવા you@example.com",
    "login_password_label": "પાસવર્ડ",
    "login_forgot": "પાસવર્ડ ભૂલી ગયા?",
    "login_btn": "સાઈન ઈન કરો",
    "login_btn_loading": "સાઈન ઈન થઈ રહ્યું છે…",
    "login_no_account": "ખાતું નથી?",
    "login_signup": "સાઈન અપ કરો",

    // Agreement Form Builder (Gujarati)
    "form_step_0_title": "ચરણ 0: તમારો કરાર પ્રકાર પસંદ કરો",
    "form_step_0_description": "તમારી જરૂરિયાતો માટે શ્રેષ્ઠ કરાર ફોર્મેટ પસંદ કરો।",
    "form_step_1_title": "ચરણ 1: માલિક વિગતો",
    "form_step_2_title": "ચરણ 2: ભાડેકર વિગતો",
    "form_step_4_title": "ચરણ 4: ભાડે શરતો",
    "form_step_5_title": "ચરણ 5: કરાર મુદત અને અંતિમ તારીખ",
    "form_step_8_title": "ચરણ 8: કરાર શેર કરો",
    "form_estamped_title": "ઈ-સ્ટ્yampપ કરાર",
    "form_eagreement_title": "ઈ-કરાર",
    "form_full_name": "સંપૂર્ણ નામ",
    "form_start_date": "શરૂઆતની તારીખ",
    "form_end_date": "અંતિમ તારીખ",
    "form_back": "← પાછળ",
    "form_next": "આગળ →",
    "form_submit": "કરાર બનાવો",

    // Main Dashboard UI (Gujarati)
    "page_create_agreement": "કરાર બનાવો",
    "page_fill_details": "કરારની વિગતો ભરો. લાઇવ પૂર્વાવલોકન રીয়েल-ટાઇમમાં અપડેટ થતું જુઓ.",
    "page_select_state": "કરાર ટેમ્પલેટ માટે રાજ્ય પસંદ કરો",
    "page_select_state_desc": "યોગ્ય કરાર ટેમ્પલેટ લોડ કરવા માટે તમારું રાજ્ય પસંદ કરો. વિવિધ રાજ્યોમાં પટ્ટા કરારો માટે વિવિધ કાયદાકીય આવશ્યકતાઓ છે.",
    "page_select_state_label": "📍 તમારું રાજ્ય પસંદ કરો",
    "page_agreement_format": "📋 કરાર ફોર્મેટ",
    "page_send_invites": "📧 કરાર શેર કરો",
    "page_pdf_preview": "📄 કરાર પૂર્વાવલોકન અને ડાઉનલોડ",
    "page_registration_status": "નોંધણી સ્થિતિ",
    "page_landlord": "🏠 મક્કીદાર",
    "page_tenant": "👤 ભાડેકર",
    "page_ready_to_sign": "સહી કરવા માટે તૈયાર",
    "page_registered": "✓ નોંધાયેલ",
    "page_not_registered": "○ નોંધાયેલ નથી",

    // Sidebar Navigation (Gujarati)
    "sidebar_home": "હોમ",
    "sidebar_overview": "સંક્ષિપ્ત",
    "sidebar_create_agreement": "કરાર બનાવો",
    "sidebar_my_team": "મારી ટીમ",
    "sidebar_my_deals": "મારા ડીલ",
    "sidebar_all_deals": "બધા ડીલ",
    "sidebar_my_earnings": "મારી આવક",
    "sidebar_my_rental": "મારું ભાડું",
    "sidebar_deposits": "ડિપોઝિટ",
    "sidebar_wallet": "વૉલેટ",
    "sidebar_notifications": "સૂચનાઓ",
    "sidebar_analytics": "વિશ્લેષણ",
    "sidebar_business_profile": "બિઝનેસ પ્રોફાઇલ",
    "sidebar_referral": "રેફરલ",
    "sidebar_profile": "પ્રોફાઇલ",
    "sidebar_settings": "સેટિંગ્સ",
    "sidebar_support": "સપોર્ટ",
    "sidebar_help": "મદદ",
  },

  pa: {
    // Navigation
    "nav_for_tenants": "ਕਿਰਾਏਦਾਰਾਂ ਲਈ",
    "nav_for_landlords": "ਮਾਲਕਾਂ ਲਈ",
    "nav_for_brokers": "ਬ੍ਰੋਕਰ ਅਤੇ ਏਜੰਟਾਂ ਲਈ",
    "nav_trust": "ਵਿਸ਼ਵਾਸ",
    "nav_about": "ਸਾਡੇ ਬਾਰੇ",
    "nav_contact": "ਸੰਪਰਕ",
    "nav_login": "ਲਾਗਇਨ",
    "nav_get_started": "ਮੁਫ਼ਤ ਸ਼ੁਰੂ ਕਰੋ",

    // Hero & Main
    "no_deposit_fights": "ਕੋਈ ਜਮ੍ਹਾ ਲੜਾਈ ਨਹੀਂ।",
    "renting_without_fights": "ਕਰਾਏ 'ਤੇ, ਬਿਨਾਂ ਲੜਾਈ ਦੇ।",
    "hero_sub": "Orello ਕਰਾਰ, ਜਮ੍ਹਾ ਅਤੇ ਸੰਪਤੀ ਰਿਪੋਰਟ ਸੰਭਾਲਦਾ ਹੈ — ਸਭ ਲਈ ਨਿਆਂ।",
    "get_started": "ਮੁਫ਼ਤ ਸ਼ੁਰੂ ਕਰੋ",
    "see_how": "ਦੇਖੋ ਕਿ ਇਹ ਕਿਵੇਂ ਕੰਮ ਕਰਦਾ ਹੈ",
    "login": "ਲਾਗਇਨ",
    "sign_up": "ਸਾਇਨ ਅਪ",
    "create_free_account": "ਮੁਫ਼ਤ ਖਾਤਾ ਬਣਾਓ",

    // For Tenants
    "your_deposit_deserves": "ਤੁਹਾਡੀ ਜਮ੍ਹਾ ਵਾਪਸ ਆਉਣ ਦੀ ਲਾਇਕ ਹੈ।",
    "deposit_protected": "₹45,000+ ਜਮ੍ਹਾ ਸੁਰੱਖਿਆ",
    "avg_resolution": "7 ਦਿਨ ਔਸਤ ਹਲ",
    "success_rate": "99.2% ਵਿਵਾਦ ਹਲ",
    "deposit_in_escrow": "ਏਸਕਰੋ ਵਿੱਚ ਜਮ੍ਹਾ",
    "proof_locked": "ਪਹਿਲੇ ਦਿਨ ਪ੍ਰਮਾਣ ਲਾਕ",
    "you_approve": "ਤੁਸੀਂ ਹਰ ਕਟੌਤੀ ਨੂੰ ਮਨਜ਼ੂਰ ਕਰਦੇ ਹੋ",
    "disputes_resolved": "10 ਦਿਨ ਵਿਵਾਦ ਹਲ",

    // For Landlords
    "rent_with_confidence": "ਭਰੋਸੇ ਨਾਲ ਕਰਾਏ 'ਤੇ ਦਿਓ।",
    "properties_listed": "12,000+ ਪੁਸ਼ਟੀ ਕੀਤੀ ਸੰਪਤੀ",
    "avg_deduction": "₹8,500 ਔਸਤ ਕਟੌਤੀ",
    "paperwork_free": "100% ਡਿਜੀਟਲ ਪ੍ਰਕਿਰਿਆ",
    "verified_tenants": "ਪੁਸ਼ਟੀ ਕੀਤੇ ਕਿਰਾਏਦਾਰ",
    "court_valid": "ਅਦਾਲਤ-ਵਿਧਿ ਕਰਾਰ",
    "property_documented": "ਸੰਪਤੀ ਦਸਤਾਵੇਜ਼",
    "clean_exits": "ਸਾਫ਼ ਬਾਹਰ ਨਿਕਲਣਾ",

    // For Brokers
    "close_more_deals": "ਵਧੇਰੇ ਡੀਲ ਬੰਦ ਕਰੋ। ਘੱਟ ਲੜਾਈ।",
    "brokers_partnered": "8,500+ ਕਿਰਿਆਸ਼ੀਲ ਏਜੰਟ",
    "avg_earnings": "₹4,500 ਪ੍ਰਤੀ ਡੀਲ",
    "faster_closures": "5 ਮਿਨਟ ਸਤਾਈ ਤੋਂ ਹਸਤਾਖਰ",
    "earn_on_deals": "ਹਰ ਡੀਲ 'ਤੇ ਕਮਾਓ",

    // Trust & Security
    "we_protect_everyone": "ਅਸੀਂ ਸਭ ਦੀ ਸੁਰੱਖਿਆ ਕਰਦੇ ਹਾਂ।",
    "regulated_escrow": "ਨਿਯੰਤ੍ਰਿਤ ਏਸਕਰੋ",
    "legal_agreements": "ਕਾਨੂੰਨੀ ਕਰਾਰ",
    "timestamped_records": "ਸਮਾਂ ਮੋਹਰ ਵਾਲੇ ਰਿਕਾਰਡ",

    // Common
    "language": "ਭਾਸ਼ਾ",
    "tenants": "ਕਿਰਾਏਦਾਰ",
    "landlords": "ਮਾਲਕ",
    "brokers": "ਬ੍ਰੋਕਰ",

    // Login page
    "login_welcome": "ਸਵਾਗਤ ਹੈ ਪਾਸਬੇਕ",
    "login_subtitle_default": "ਆਪਣੇ ਫ਼ੋਨ ਜਾਂ ਈਮੇਲ ਅਤੇ ਪਾਸਵਰਡ ਨਾਲ ਸਾਇਨ ਇਨ ਕਰੋ।",
    "login_subtitle_tenant": "ਆਪਣੀ ਜਮ੍ਹਾ ਰੱਖਣ ਲਈ ਸਾਇਨ ਇਨ ਕਰੋ",
    "login_subtitle_landlord": "ਆਪਣੇ ਕਿਰਾਏਦਾਰ ਦੀ ਪੁਸ਼ਟੀ ਕਰਨ ਲਈ ਸਾਇਨ ਇਨ ਕਰੋ",
    "login_subtitle_broker": "ਹਰ ਡੀਲ 'ਤੇ ਕਮਿਸ਼ਨ ਲੱਭਣ ਲਈ ਸਾਇਨ ਇਨ ਕਰੋ",
    "login_phone_label": "ਫ਼ੋਨ ਜਾਂ ਈਮੇਲ",
    "login_phone_placeholder": "9876543210 ਜਾਂ you@example.com",
    "login_password_label": "ਪਾਸਵਰਡ",
    "login_forgot": "ਪਾਸਵਰਡ ਭੁੱਲ ਗਏ?",
    "login_btn": "ਸਾਇਨ ਇਨ ਕਰੋ",
    "login_btn_loading": "ਸਾਇਨ ਇਨ ਹੋ ਰਿਹਾ ਹੈ…",
    "login_no_account": "ਖਾਤਾ ਨਹੀਂ?",
    "login_signup": "ਸਾਇਨ ਅਪ ਕਰੋ",

    // Agreement Form Builder (Punjabi)
    "form_step_0_title": "ਸਤਰ 0: ਆਪਣੇ ਕਰਾਰ ਦੀ ਕਿਸਮ ਚੁਣੋ",
    "form_step_0_description": "ਆਪਣੀ ਲੋੜ ਅਨੁਸਾਰ ਸਭ ਤੋਂ ਸੁਲੱਖ ਕਰਾਰ ਫ਼ਾਰਮੈਟ ਚੁਣੋ।",
    "form_step_1_title": "ਸਤਰ 1: ਮਾਲਕ ਦੀ ਵਿਸਤ੍ਰਿਤ ਜਾਣਕਾਰੀ",
    "form_step_2_title": "ਸਤਰ 2: ਕਿਰਾਏਦਾਰ ਦੀ ਵਿਸਤ੍ਰਿਤ ਜਾਣਕਾਰੀ",
    "form_step_4_title": "ਸਤਰ 4: ਕਰਾਏ ਦੀਆਂ ਸ਼ਰਤਾਂ",
    "form_step_5_title": "ਸਤਰ 5: ਕਰਾਰ ਦੀ ਮਿਆਦ ਅਤੇ ਅੰਤਮ ਤਾਰੀਖ",
    "form_step_8_title": "ਸਤਰ 8: ਕਰਾਰ ਸਾਂਝਾ ਕਰੋ",
    "form_estamped_title": "ਈ-ਸਟੈਂਪ ਕਰਾਰ",
    "form_eagreement_title": "ਈ-ਕਰਾਰ",
    "form_full_name": "ਪੂਰਾ ਨਾਮ",
    "form_start_date": "ਸ਼ੁਰੂ ਕਰਨ ਦੀ ਤਾਰੀਖ",
    "form_end_date": "ਅੰਤਮ ਤਾਰੀਖ",
    "form_back": "← ਪਿੱਛੇ",
    "form_next": "ਅੱਗੇ →",
    "form_submit": "ਕਰਾਰ ਬਣਾਓ",

    // Main Dashboard UI (Punjabi)
    "page_create_agreement": "ਕਰਾਰ ਬਣਾਓ",
    "page_fill_details": "ਕਰਾਰ ਦੀ ਵਿਸਤ੍ਰਿਤ ਜਾਣਕਾਰੀ ਭਰੋ। ਲਾਇਵ ਪ੍ਰਿਵਿਊ ਰਿਅਲ-ਟਾਈਮ ਵਿੱਚ ਅੱਪਡੇਟ ਹੋਉਂਦਾ ਦੇਖੋ।",
    "page_select_state": "ਕਰਾਰ ਟੈਮਪਲੇਟ ਲਈ ਸੂਬਾ ਚੁਣੋ",
    "page_select_state_desc": "ਢੁਕਵੀਂ ਕਰਾਰ ਟੈਮਪਲੇਟ ਲੋਡ ਕਰਨ ਲਈ ਆਪਣਾ ਸੂਬਾ ਚੁਣੋ। ਵੱਖ-ਵੱਖ ਸੂਬਿਆਂ ਲਈ ਕਰਾਏ ਦੇ ਕਰਾਰਾਂ ਦੀਆਂ ਕਾਨੂੰਨੀ ਜ਼ਰੂਰਤਾਂ ਵੱਖ-ਵੱਖ ਹਨ।",
    "page_select_state_label": "📍 ਆਪਣਾ ਸੂਬਾ ਚੁਣੋ",
    "page_agreement_format": "📋 ਕਰਾਰ ਫਾਰਮੈਟ",
    "page_send_invites": "📧 ਕਰਾਰ ਸਾਂਝਾ ਕਰੋ",
    "page_pdf_preview": "📄 ਕਰਾਰ ਪ੍ਰਿਵਿਊ ਅਤੇ ਡਾਊਨਲੋਡ",
    "page_registration_status": "ਰਜਿਸਟ੍ਰੇਸ਼ਨ ਸਥਿਤੀ",
    "page_landlord": "🏠 ਮਾਲਕ",
    "page_tenant": "👤 ਕਿਰਾਏਦਾਰ",
    "page_ready_to_sign": "ਦਸਖਤ ਲਈ ਤਿਆਰ",
    "page_registered": "✓ ਰਜਿਸਟਰਡ",
    "page_not_registered": "○ ਰਜਿਸਟਰਡ ਨਹੀਂ",

    // Sidebar Navigation (Punjabi)
    "sidebar_home": "ਘਰ",
    "sidebar_overview": "ਜਾਣਕਾਰੀ",
    "sidebar_create_agreement": "ਕਰਾਰ ਬਣਾਓ",
    "sidebar_my_team": "ਮੇਰੀ ਟੀਮ",
    "sidebar_my_deals": "ਮੇਰੇ ਡੀਲ",
    "sidebar_all_deals": "ਸਾਰੇ ਡੀਲ",
    "sidebar_my_earnings": "ਮੇਰੀ ਕਮਾਈ",
    "sidebar_my_rental": "ਮੇਰਾ ਕਰਾਇਆ",
    "sidebar_deposits": "ਜਮਾਨਤਾਂ",
    "sidebar_wallet": "ਵਾਲਿਟ",
    "sidebar_notifications": "ਸੂਚਨਾਵਾਂ",
    "sidebar_analytics": "ਵਿਸ਼ਲੇਸ਼ਣ",
    "sidebar_business_profile": "ਵਪਾਰਕ ਪ੍ਰੋਫਾਈਲ",
    "sidebar_referral": "ਹਵਾਲਾ",
    "sidebar_profile": "ਪ੍ਰੋਫਾਈਲ",
    "sidebar_settings": "ਸੈਟਿੰਗਾਂ",
    "sidebar_support": "ਸਹਾਇਤਾ",
    "sidebar_help": "ਮਦਦ",
  },
};

const I18nContext = createContext({ t: (k) => k, lang: "en", setLang: () => {} });

export function I18nProvider({ children }) {
  const [lang, setLang] = useState(localStorage.getItem("orello_lang") || "en");
  const setLangPersist = useCallback((l) => {
    localStorage.setItem("orello_lang", l);
    setLang(l);
  }, []);
  const t = useCallback((k) => (dict[lang] && dict[lang][k]) || dict.en[k] || k, [lang]);
  const value = useMemo(() => ({ t, lang, setLang: setLangPersist }), [t, lang, setLangPersist]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export const useI18n = () => useContext(I18nContext);
